/*
 * Copyright (c) 2019 Pegasystems Inc.
 * All rights reserved.
 */
import { C11nEnv } from '../interpreter/c11n-env';
import RemoteCase from '../case/remote-case';
import Constants, { publicConstants } from '../constants';
import ContextProcessor from '../context/context-processor';
import { addMessages, clearMessages } from '../messages';
import RouteParser from '../router/route-parser';
import { getResolvedTargetName } from '../router/routing-manager';
import services from '../services';
import { fetch } from '../service_broker/fetch';
import { FIELD_CHANGE, SET_PROPERTY } from '../store/state/actions';
import * as actionType from '../store/state/actions/index';
import ContextTreeManager from '../contextree/treemanager';
import {
  CONTAINER_NAMES,
  getDataContextName,
  getChildContainers,
  getContainerData,
  getActiveContainerItemName,
  getContainerItemData,
  getTargetFromContainerItemID,
  getParentContainerItem
} from '../container/container-utils';
import { isContextDirty } from '../store/state/middlewares/property-change-middleware';
import * as StateManager from '../store/state/state-manager';
import PubSubUtils from '../utils/pubsub-utils';
import {
  isAssignmentInCreateStage,
  isChildCase,
  isLocalAction,
  isAssignment,
  isCaseWideAction,
  getContainerDataForFinishAssignment,
  getActionIdForFinishAssignment,
  getFinishAssignmentActionType
} from '../utils/case-utils';
import { isFormHasSuggestions, isFormValid } from '../utils/form-handler';
import { OPEN_PROCESS_ACTION } from '../case/open-process-action/action-creator';
import { OPEN_ASSIGNMENT } from '../case/open-assignment/action-creator';
import {
  deleteCaseInCreateStageAction,
  DELETE_CASE_IN_CREATE_STAGE
} from '../case/delete-case-in-create-stage/action-creator';
import { saveAndCloseAction, SAVE_AND_CLOSE } from '../case/save-and-close/action-creator';
import {
  dispatchCaseViewRefresh,
  dispatchRefreshForEmbeddeddataWithAction,
  refreshAssignment
} from '../refresh/refreshHelpers';
import { getContext, SCOPE_TYPES } from '../store/state/epics/util';
import { deleteDataObjectAction } from '../data/delete-data-object/action-creator';
import { deleteWorkAction } from '../object/delete-work/action-creator';
import DataTypeUtils from '../datatype';
import ContainerManager from '../container/container-manager';
import { createDataObjectAction, getDataObjectViewAction, updateDataObjectAction } from '../data';
import Utils from '../utils/utils';
import Debugger from '../debug/debugger';
import EnvironmentInfo from '../environment-info';
import utils from '../contextree/utils';
import type {
  ActionAPIOptions,
  ActionInfo,
  ActionObject,
  CaseActionOptions,
  Component,
  ExtendedActionObject,
  RefreshAPIOptions,
  RemoteCaseMeta
} from '../globals';
import GarbageCollector from '../garbage-collector';
import CaseUtils from '../case/utils';
import { getDescendantsAction } from '../case/get-descendants/action-creator';
import { getPropertyMetadata, resolveView } from '../rules/rules-store';
import AnnotationProcessor from '../annotation/processor';
import type { PropertyModel } from '../rules/types';
import refreshConstants from '../refresh';
import HeaderProcessor from '../service_broker/header-processor';
import RuntimeParamsAPI from '../runtime-params';

import type { LoadViewConfig, RefreshCaseView, JSActionQueryParams, PreviewOptions } from './types';
import ActionProcessor from './processor';
import { ActionManagerInstance } from './manager';
import ActionsSequencer from './sequencer';
import TargetMap from './targetmap';

const getLookupDataPageDetails = (className: string, keyProperties: { [key: string]: string }) => {
  const dataViewParameters: { [key: string]: string } = {};
  const dataPageID = DataTypeUtils.getLookUpDataPage(className);
  const dataPageKeys = DataTypeUtils.getDataPageKeys(dataPageID);
  if (dataPageKeys) {
    for (const dataPageKey of dataPageKeys) {
      dataViewParameters[dataPageKey.keyName] = dataPageKey.isAlternateKeyStorage
        ? keyProperties[dataPageKey.linkedField]
        : keyProperties[dataPageKey.keyName];
    }
  }
  return {
    dataViewParameters,
    dataPageID,
    dataPageKeys
  };
};

const getSavableDataPageDetails = (className: string, keys: { [key: string]: string }) => {
  const dataPageID = DataTypeUtils.getSavableDataPage(className);
  const dataPageKeys = DataTypeUtils.getDataPageKeys(dataPageID);
  dataPageKeys?.forEach((dataPageKey): void => {
    if (
      dataPageKey.isAlternateKeyStorage &&
      (dataPageKey.keyName === dataPageKey.linkedField || Object.keys(keys).includes(dataPageKey.linkedField))
    ) {
      // Scenario -1 : With AlternateKey storage - paramName is same as linkedField
      // Scenario -2 : With AlternateKey storage - ParamName as any of the field and LinkedField as class key
      dataPageKey.keyName = keys[dataPageKey.linkedField];
    } else if (
      dataPageKey.isAlternateKeyStorage &&
      Object.keys(keys).includes(dataPageKey.keyName) &&
      dataPageKey.keyName !== dataPageKey.linkedField
    ) {
      // Scenario -3 : With AlternateKey storage - ParamName as  the key and LinkedField as any of the filed
      keys[dataPageKey.linkedField] = keys[dataPageKey.keyName];
      delete keys[dataPageKey.keyName];
    } else {
      // Scenario -4 : Without AlternateKey storage
      dataPageKey.keyName = keys[dataPageKey.keyName];
    }
  });
  return {
    keys,
    dataPageID,
    dataPageKeys
  };
};

const publishCancelEvent = (data: object) => {
  PubSubUtils.publish(publicConstants.PUB_SUB_EVENTS.EVENT_CANCEL, data);
};

const invokePMCAction = (
  actionFunc: (actionId: string, options: CaseActionOptions) => Promise<string | undefined>,
  actionId: string,
  options: CaseActionOptions
) => {
  const { callbacks } = options;
  options.callbacks = null; // callbacks not supported directly by iOS bridge
  return actionFunc(actionId, options).then((result: string | undefined) => {
    if (result === 'cancel') {
      callbacks?.cancel?.();
    } else {
      callbacks?.submit?.();
    }
  });
};

const getCreateWorkType = (className: string, isExternalObjectCreationFlow: boolean) => {
  if (CaseUtils.isExternalObject(className) && !isExternalObjectCreationFlow) {
    return actionType.CREATE_EXTERNAL_OBJECT;
  }
  return CaseUtils.isObjectCaseType(className) ? actionType.CREATE_OBJECT : actionType.CREATE_WORK;
};

const getLocalActionType = (options: CaseActionOptions) => {
  if (
    options.type === 'Object' ||
    CaseUtils.isObjectCaseType(options?.caseClassName || '') ||
    window.location.href.includes('/objects/')
  ) {
    return actionType.OPEN_OBJECT_ACTION;
  }
  return actionType.OPEN_LOCAL_ACTION;
};

const getActionID = (component: Component, editType: string, localAction: boolean) => {
  return !localAction && editType === Constants.ACTION
    ? component.getValue(publicConstants.CASE_INFO.ASSIGNMENTACTION_ID)
    : component.getValue(publicConstants.CASE_INFO.ACTIVE_ACTION_ID);
};

/**
 * Provides the basic APIs to handle actions in the Constellation infrastructure
 */
class ActionsApi {
  MSG_CLASSNAME_REQUIRED = 'className is required parameter';
  MSG_CLASSNAME_STRING = 'className must be a string';
  MSG_ACTION_ID_REQUIRED = 'actionID is required parameter';
  MSG_ACTION_ID_STRING = 'actionID must be a string';
  MSG_ACTION_NAME_REQUIRED = 'actionName is required parameter';
  MSG_ACTION_NAME_STRING = 'actionName must be a string';
  MSG_KEY_PROPERTIES_REQUIRED = 'keyProperties is required parameter';
  MSG_DATAVIEWID_REQUIRED = 'dataViewID is required parameter';
  MSG_DATAVIEWID_STRING = 'dataViewID must be a string';
  MSG_CONTAINERITEMID_REQUIRED = 'containerItemID is required parameter';
  MSG_CONTAINERITEMID_STRING = 'containerItemID must be a string';
  MSG_PAGENAME_REQUIRED = 'pageName is required parameter';
  MSG_PAGENAME_STRING = 'pageName must be string';
  MSG_INSIGHTID_REQUIRED = 'insightID is required parameter';
  MSG_DASHBOARDID_REQUIRED = 'DashboardID is required parameter';
  MSG_ACTIVE_SUGGESTION_UNATTENDED = 'You must accept or decline autofilled fields.';
  MSG_CASE_ID_REQUIRED = 'caseID is required parameter';
  MSG_CONTEXT_STRING = 'context must be string';
  MSG_SUBSCRIPT_VIEW_REQUIRED = 'subscriptView is required';
  /**
   * Constructor - <strong>Not for use outside of Core! Included for reference only.</strong>
   * @param c11nEnv The context object that is used for this instance of the ActionsApi
   */

  private readonly c11nEnv: C11nEnv;

  constructor(c11nEnv: C11nEnv) {
    this.c11nEnv = c11nEnv;
  }

  /**
   * Returns should delete case in create stage or not
   * @param isFormContextDirty - boolean flag for form dirty.
   * @param isFirstAssignment - boolean flag for first assignment.
   * @param isCoexistenceUIKITApp - boolean flag for uikit app.
   * @returns Returns true If the assignment is in create stage, first assignment and context is not dirty
   * @function
   * @private
   */
  shouldDeleteCaseInCreateStage = (
    isFormContextDirty: boolean,
    isFirstAssignment: boolean,
    isCoexistenceUIKITApp: boolean
  ): boolean => (!isFormContextDirty && isFirstAssignment) || isCoexistenceUIKITApp;

  /**
   * Retrieves the case id, if not present then undefined
   * @param options CaseActionOptions
   * @returns caseID or undefined
   * @private
   */
  getCaseID = (options: CaseActionOptions): string | undefined => {
    return (
      options.caseID ||
      this.c11nEnv.getValue(Constants.getProperty(publicConstants.CASE_INFO.CASE_INFO_ID)) ||
      options?.links?.open?.href?.split('/')?.[2]
    );
  };

  /**
   * Returns payload for SAVE_AND_CLOSE action
   * @param containerItemID - Id of the container item.
   *
   * For example: "app/modal_1"
   * @param saveOnly parameter which determines whether to only save the assignment.
   * @returns Payload required for SAVE_AND_CLOSE action
   * @function
   * @private
   */
  getSaveActionPayload = (containerItemID: string, saveOnly = false): { [key: string]: any } => {
    const context = containerItemID;
    const isModalAction = context && context.includes(Constants.MODAL);
    const caseID = this.c11nEnv.getValue(publicConstants.CASE_INFO.CASE_INFO_ID);
    const assignmentID = this.c11nEnv.getValue(publicConstants.CASE_INFO.ASSIGNMENT_ID);
    const actionID = this.c11nEnv.getValue(publicConstants.CASE_INFO.ACTIVE_ACTION_ID)
      ? this.c11nEnv.getValue(publicConstants.CASE_INFO.ACTIVE_ACTION_ID)
      : this.c11nEnv.getValue(publicConstants.CASE_INFO.ASSIGNMENTACTION_ID);
    const containerName = this.c11nEnv.getContainerName();
    const caseType = this.c11nEnv.getValue(publicConstants.CASE_INFO.CASE_TYPE_ID);
    const isFormValidFlag = saveOnly ? undefined : isFormValid(this.c11nEnv.getContextName());
    const bIsProcessAction = this.c11nEnv.getValue('isProcessAction', 'isProcessAction');
    return ActionProcessor.buildActionObject(
      {
        context,
        containerItemID,
        assignmentID,
        actionID,
        caseID,
        containerName,
        caseType,
        isModalAction,
        isFormValid: isFormValidFlag,
        isProcessAction: bIsProcessAction,
        saveOnly
      },
      this.c11nEnv
    );
  };

  /**
   * Create a promise for an actionsApi action and return an object containing the promise
   * and the ActionManager actionMgrID associated with that promise
   * @param theActionType the action type for which we're creating a promise
   * @param actionConfig the payload of the action being registered
   * @param enableLoadingIndicator if true (the default), call the IsLoadingManager's enableLoadingIndicator
   * while registering the promise with the ActionManager. If false, do not call enableLoadingIndicator
   * @returns object with actionMgrID and promise keys: { actionMgrID: <theID>, promise: <thePromise> }
   * @function
   * @private
   */

  actionsApiPromise = (
    theActionType: string,
    actionConfig?: ActionObject,
    enableLoadingIndicator = true
  ): { actionMgrID: string; promise: Promise<object> } => {
    let theActionMgrID = '';
    if (actionConfig && actionConfig.actionMgrID !== null) {
      theActionMgrID = actionConfig.actionMgrID || '';
    } else {
      theActionMgrID = ActionManagerInstance.getNextActionMgrId();
    }
    let resolveFn;
    let rejectFn;
    const thePromise: Promise<object> = new Promise((resolve, reject) => {
      resolveFn = resolve;
      rejectFn = reject;
    });

    if (resolveFn && rejectFn) {
      ActionManagerInstance.register(
        theActionMgrID,
        thePromise,
        resolveFn,
        rejectFn,
        theActionType,
        actionConfig,
        enableLoadingIndicator
      );
    } else {
      throw new Error('resolveFn or rejectFn is not defined.');
    }

    return {
      actionMgrID: theActionMgrID,
      promise: thePromise
    };
  };

  /**
   * Displays the create view of a data object to be created.
   * <br>
   *
   * @example <caption>Example for showDataObjectCreateView()</caption>
   * Example usage -
   * getPConnect().getActionsApi().showDataObjectCreateView('OZR2SN-ReactApp-Data-Person', 'Create').then(() => {
   *  // showDataObjectCreateView success handling
   * }).catch(() => {
   *  // showDataObjectCreateView failure handling
   * })
   *
   * @param className The name of the class that provides the type that the data object should belong to.
   *
   * @param viewName The name of the create view that is to be displayed for the data object.
   *
   * @returns A Promise associated with the action.
   * <br><br>
   */
  showDataObjectCreateView(className: string, viewName: string = publicConstants.VIEW_NAMES.DATA_OBJECT_CREATE_VIEW) {
    const actionConfig = ActionProcessor.buildActionObject(
      {
        viewName,
        className
      },
      this.c11nEnv
    );
    const theType = actionType.SHOW_PAGE;
    const { promise } = this.actionsApiPromise(theType, actionConfig);

    StateManager.dispatch({
      type: theType,
      payload: {
        ...actionConfig,
        containerName: publicConstants.MODAL,
        skipSemanticUrl: true,
        isCreateDataObject: true
      }
    });
    // return the Promise associated with this action
    return promise;
  }

  /**
   * Cancels creation of data object by showing cancel alert popup in case any changes were made.
   * If no changes were made it removes the container item in which create view of DataType is loaded
   * <br>
   *
   * @example <caption>Usage example</caption>
   * In this example, the success callback is called if the creation of the data object is canceled.
   * const cancelCreateDataObjectPromise = getPConnect().getActionsApi().cancelDataObject("app/modal_3");
   * cancelCreateDataObjectPromise.then(() => {
   *  // cancel creation of data object success handling
   * }).catch(() => {
   *  // cancel creation of data object failure handling
   * });
   *
   * @param containerItemID The ID of the container item containing the create view of the data object.
   * <br><br>
   * @returns A Promise associated with the action.
   *
   */
  cancelDataObject(containerItemID: string): Promise<object> | object {
    if (!containerItemID) {
      return Promise.reject(new Error('containerItemId is required parameter'));
    }
    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }
    const context = containerItemID;
    const isFormContextDirty = isContextDirty(context);
    if (!isFormContextDirty) {
      const containerManager = new ContainerManager(this.c11nEnv);
      return containerManager.removeContainerItem({
        containerItemID,
        skipReleaseLockRequest: true
      });
    }
    const theType = actionType.SHOW_CANCEL_ALERT;
    const actionPayload = ActionProcessor.buildActionObject({ containerItemID, context }, this.c11nEnv);
    const isModalAction = context && context.includes(Constants.MODAL);
    StateManager.dispatch({
      type: theType,
      payload: { ...actionPayload, isModalAction, isDataObject: true, skipReleaseLockRequest: true }
    });
    const { promise } = this.actionsApiPromise(theType, actionPayload);
    return promise.then(() => {
      publishCancelEvent({});
    });
  }

  /**
   * Creates a data object whose create view is loaded in the specified container item.
   * <br>
   *
   * @example <caption>In this example, the success callback is called if the data object is created.</caption>
   *
   * const createDataObjectPromise = getPConnect().getActionsApi().createDataObject("app/modal_3");
   * createDataObjectPromise.then(() => {
   *  // create data object success handling
   * }).catch(() => {
   *  // create data object failure handling
   * });
   *
   * @param containerItemID The ID of the container item containing the create view of the data object.
   * <br><br>
   * @returns A Promise associated with the action.
   *
   */
  createDataObject(containerItemID: string) {
    if (!containerItemID) {
      return Promise.reject(new Error('containerItemID is required parameter'));
    }
    const { data } = StateManager.getStore()?.getState() || {};
    const className = data[containerItemID]?.dataInfo?.content?.classID;
    if (!className) {
      return Promise.reject(new Error(Constants.DATA_CLASS_NOT_FOUND));
    }
    type ResolvedSubscriptView = { name: string } | object[];
    const resolvedSubscriptView = resolveView(
      publicConstants.VIEW_NAMES.SUBSCRIPT_CREATE_VIEW
    ) as ResolvedSubscriptView;

    /**
     * Calls the `handleSubscript` function in Page group for the given container item ID and context environment.
     * If `handleSubscript` returns a promise, it returns that promise to the caller.
     *
     */
    if (resolvedSubscriptView) {
      const subscriptPromise = this.handleSubscript.call(this, containerItemID, this.c11nEnv);
      if (subscriptPromise) {
        return subscriptPromise;
      }
    }
    const dataPageID = DataTypeUtils.getSavableDataPage(className);
    const theType = actionType.CREATE_DATA_OBJECT;
    const isModalAction = !!containerItemID && containerItemID.includes(Constants.MODAL);
    const actionConfig = ActionProcessor.buildActionObject(
      {
        containerItemID,
        dataPageID
      },
      this.c11nEnv
    );
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    StateManager.dispatch(
      createDataObjectAction({
        ...actionConfig,
        isModalAction,
        isFormValid: isFormValid(containerItemID)
      })
    );

    // return the Promise associated with this action
    return promise;
  }

  /**
   * Deletes data object of specific type with object containing primary keys
   * <br>
   *
   * @example <caption>In this example, the success callback is called if the data object is deleted.</caption>
   *
   * getPConnect().getActionsApi().deleteDataObject('APP-ReactApp-Data-Person', { pyGUID: '3ba585e8-f3e2-4404-8a15-692992de53b4' }).then(() => {
   *  // deleteDataObject success handling
   * }).catch(() => {
   *  // deleteDataObject failure handling
   * });
   *
   * @param className The name of the class that provides the type that the data object should belong to.
   *
   * @param keys Object of the primary keys with values for the data object.
   *
   * @returns A Promise associated with the action.
   */
  deleteDataObject(className: string, keys: { [key: string]: any }) {
    if (!className) {
      return Promise.reject(new Error('className is required parameter'));
    }
    if (!keys) {
      return Promise.reject(new Error('keys is required parameter'));
    }
    const theType = actionType.DELETE_DATA_OBJECT;
    const dataViewName = DataTypeUtils.getSavableDataPage(className);
    const dataPageKeys = DataTypeUtils.getDataPageKeys(dataViewName);
    const dataViewParameters: { [key: string]: object | string } = {};
    if (dataPageKeys) {
      for (const dataPageKey of dataPageKeys) {
        dataViewParameters[dataPageKey.keyName] = dataPageKey.isAlternateKeyStorage
          ? keys[dataPageKey.linkedField]
          : keys[dataPageKey.keyName];
      }
    }
    const actionConfig = ActionProcessor.buildActionObject(
      {
        dataViewName,
        dataViewParameters
      },
      this.c11nEnv
    );
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    StateManager.dispatch(deleteDataObjectAction({ ...actionConfig }));
    return promise;
  }

  /**
   * Displays the edit view of a data object to be updated.
   * <br>
   *
   * @example <caption>Example for getDataObjectView()</caption>
   * Example usage -
   * getPConnect().getActionsApi().getDataObjectView('OZR2SN-ReactApp-Data-Person', {pyGUID: "61a8e531-afcb-41f7-bd72-9809e1a3cbe9"}).then(() => {
   *  // getDataObjectView success handling
   * }).catch(() => {
   *  // getDataObjectView failure handling
   * })
   *
   * @param className The name of the class that provides the type that the data object should belong to.
   * @param keyProperties The object which contains the key properties of the dataObject
   * @param options Javascript object contains the view information
   * @param options.viewName The name of the edit view that is to be displayed for the data object.
   * @param options.readOnly If passed as true, the view will be rendered as readOnly
   * @param options.actionName The name of the action that should be displayed in the modal title.
   *
   * @returns A Promise associated with the action.
   */
  getDataObjectView(
    className: string,
    keyProperties: { [key: string]: string },
    options: { viewName: string; readOnly: boolean; actionName: string } = {
      viewName: '',
      readOnly: false,
      actionName: ''
    }
  ) {
    if (!className) {
      return Promise.reject(new Error(this.MSG_CLASSNAME_REQUIRED));
    }

    if (typeof className !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CLASSNAME_STRING));
    }

    if (!keyProperties) {
      return Promise.reject(new TypeError(this.MSG_KEY_PROPERTIES_REQUIRED));
    }

    if (typeof keyProperties !== 'object') {
      return Promise.reject(new TypeError('keyProperties must be a object'));
    }

    const theType = actionType.VIEW_DATA_OBJECT;
    const { dataPageID, dataViewParameters } = getLookupDataPageDetails(className, keyProperties);

    const actionConfig = ActionProcessor.buildActionObject(
      {
        dataLookUp: dataPageID,
        keyProperties,
        requestPayload: dataViewParameters,
        viewName: options.viewName || publicConstants.VIEW_NAMES.DATA_OBJECT_EDIT_VIEW,
        containerName: Constants.MODAL,
        readOnly: options.readOnly,
        actionName: options.actionName
      },
      this.c11nEnv
    );

    const { promise } = this.actionsApiPromise(theType, actionConfig);

    StateManager.dispatch(
      getDataObjectViewAction({
        ...actionConfig
      })
    );
    // return the Promise associated with this action
    return promise;
  }

  /**
   * Updates a data object whose edit view is loaded in the specified container item.
   * <br>
   *
   * @example <caption>Example for updateDataObject()</caption>
   * Example usage -
   * const updateDataObjectPromise = getPConnect().getActionsApi().updateDataObject("app/modal_3", {pyGUID:"61a8e531-afcb-41f7-bd72-9809e1a3cbe9"});
   * updateDataObjectPromise.then(() => {
   *    updateDataObject success handling
   * }).catch(() => {
   *    updateDataObject failure handling
   * });
   *
   * @param containerItemID The ID of the container item containing the edit view of the data object.
   * Example: "app/modal_3"
   * @param keys The keys of the dataRecord to be updated
   * Example: {pyGUID:"b1715d32-7d7b-4d41-97ce-ced87b7c8fb0"}
   * @returns A Promise associated with the action.
   *
   */
  updateDataObject(containerItemID: string, keys: { [key: string]: any }) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }
    if (!keys) {
      return Promise.reject(new Error('keys is required parameter'));
    }
    const { data } = StateManager.getStore()?.getState() || {};
    const className = data[containerItemID]?.dataInfo?.content?.classID;
    if (!className) {
      return Promise.reject(new Error(Constants.DATA_CLASS_NOT_FOUND));
    }
    const { dataPageID, keys: keyProperties } = getSavableDataPageDetails(className, keys);
    const theType = actionType.UPDATE_DATA_OBJECT;
    const isModalAction = containerItemID && containerItemID.includes(Constants.MODAL);
    const actionConfig = ActionProcessor.buildActionObject(
      {
        containerItemID,
        dataPageID,
        key: keyProperties
      },
      this.c11nEnv
    );
    const etag = data[containerItemID]?.caseInfo?.headers?.etag;
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    StateManager.dispatch(
      updateDataObjectAction({
        ...actionConfig,
        isModalAction,
        isFormValid: isFormValid(containerItemID),
        etag
      })
    );

    // return the Promise associated with this action
    return promise;
  }

  /**
   * Displays the flow action view of a data object to be updated.
   * @example <caption>Example for openDataObjectAction()</caption>
   * Example usage -
   * const openDataObjectActionPromise = getPConnect().getActionsApi().openDataObjectAction("ON8TTL-C11nGall-Data-Card", {Number:1234}, 'updateCVV', 'Update CVV');
   * openDataObjectActionPromise.then(() => {
   *    openDataObjectAction success handling
   * }).catch(() => {
   *    openDataObjectAction failure handling
   * });
   * @param className - Class ID of the Data Type Configured.
   * @param keyProperties - values of the data type record.
   * @param actionID - ID of the Action that is configured on the Data Type.
   * @param actionName - Name of the Action that is configured on the Data Type.
   * @returns A Promise associated with the action.
   */
  openDataObjectAction(
    className: string,
    keyProperties: { [key: string]: string },
    actionID: string,
    actionName: string
  ) {
    if (!className) {
      return Promise.reject(new Error(this.MSG_CLASSNAME_REQUIRED));
    }
    if (typeof className !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CLASSNAME_STRING));
    }
    if (!keyProperties) {
      return Promise.reject(new TypeError(this.MSG_KEY_PROPERTIES_REQUIRED));
    }

    if (typeof keyProperties !== 'object') {
      return Promise.reject(new TypeError('keyProperties must be a object'));
    }
    if (!actionID) {
      return Promise.reject(new Error(this.MSG_ACTION_ID_REQUIRED));
    }

    if (!actionName) {
      return Promise.reject(new Error(this.MSG_ACTION_NAME_REQUIRED));
    }

    const theType = actionType.OPEN_DATA_OBJECT_ACTION;
    const { dataViewParameters, dataPageID, dataPageKeys } = getLookupDataPageDetails(className, keyProperties);

    const actionConfig = ActionProcessor.buildActionObject(
      {
        actionID,
        actionName,
        dataViewParameters,
        lookupDatapage: dataPageID,
        containerName: Constants.MODAL,
        dataViewPayload: dataPageKeys,
        context: this.c11nEnv.getContextName()
      },
      this.c11nEnv
    );

    const { promise } = this.actionsApiPromise(theType, actionConfig);

    StateManager.dispatch({
      type: theType,
      payload: {
        ...actionConfig
      }
    });
    // return the Promise associated with this action
    return promise;
  }

  /**
   * Updates the data object action based on flow action.
   * @example <caption>In this example, the success callback is called if the specified data object is updated based on the updateCVV flow action.</caption>
   * Example usage -
   * const submitDataObjectActionPromise = getPConnect().getActionsApi().submitDataObjectAction("app/primary_1/modal_1", '{Number:1234}', 'updateCVV');
   * submitDataObjectActionPromise.then(() => {
   *    submitDataObjectAction success handling
   * }).catch(() => {
   *    submitDataObjectAction failure handling
   * });
   * @param {string} containerItemID - The ID of the container item containing the edit view of the data object.
   * @param {Object.<string, string>} keyProperties  - The keys of the dataRecord to be updated
   * @param {string} actionID - ID of the Action that is configured on the Data Type.
   * @param {Object} [options] - (optional) The JavaScript object that contains the properties required for updating the data object.
   * @param {Object} [options.jsActionQueryParams]  - (optional) The JavaScript object that contains properties that are added as query parameters to the fetch call of the submitDataObjectAction API.
   * Note: The jsActionQueryParams object only supports the skipRoboticAutomation property as of Infinity '25.
   * Supported values:
   * true - When true, post processing robotic automation is skipped while submitting the form.
   * false - When false, post processing robotic automation is considered while submitting the form.
   * @returns A Promise associated with the action.
   */
  submitDataObjectAction(
    containerItemID: string,
    keyProperties: { [key: string]: string },
    actionID: string,
    options?: {
      jsActionQueryParams: JSActionQueryParams;
    }
  ) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }
    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }
    if (!keyProperties) {
      return Promise.reject(new Error(this.MSG_KEY_PROPERTIES_REQUIRED));
    }
    if (!actionID) {
      return Promise.reject(new Error(this.MSG_ACTION_ID_REQUIRED));
    }
    const { data } = StateManager.getStore()?.getState() || {};
    const className = data[containerItemID]?.dataInfo?.content?.classID;
    if (!className) {
      return Promise.reject(new Error(Constants.DATA_CLASS_NOT_FOUND));
    }
    const jsActionQueryParams = options?.jsActionQueryParams;
    const parentContainerItemID = getParentContainerItem(containerItemID);
    const target = getTargetFromContainerItemID(parentContainerItemID);
    const itemData = getContainerItemData(target, parentContainerItemID) || {};
    const { resourceType = '', key = '' } = itemData;
    const theType: string = actionType.SUBMIT_DATA_OBJECT_ACTION;
    const { dataPageID, keys } = getSavableDataPageDetails(className, keyProperties);
    const actionConfig = ActionProcessor.buildActionObject(
      {
        containerItemID,
        actionID,
        resourceType,
        key,
        keys,
        savableDatapage: dataPageID,
        containerName: Constants.MODAL,
        context: containerItemID,
        jsActionQueryParams
      },
      this.c11nEnv
    );
    actionConfig.context = containerItemID;
    const etag = data[containerItemID]?.caseInfo?.headers?.etag;
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    StateManager.dispatch({
      type: theType,
      payload: { ...actionConfig, isFormValid: isFormValid(containerItemID), etag }
    });
    return promise;
  }

  /**
   *
   * Creates a work object for the given <strong>className</strong> and displays the assignment.
   *
   * @example <caption>Example for createWork()</caption>
   * Example usage -
   *  const options = {
   *   flowType: "pyStartCase",
   *   containerName: "primary",
   *   startingFields: {
   *    FirstName: "Adam",
   *    LastName: "Smith",
   *    Vehicle: {
   *      Make: "Honda",
   *      Model: "Accord"
   *    }
   *  }
   * };
   *  const createWorkPromise = getPConnect().getActionsApi().createWork("OPB1HW-SpaceTra-Work-RequestApproval", options); <br>
   * createWorkPromise.then(() => {
   *   // create work success handling
   * }).catch(() => {
   *   // create work failure handling
   * });
   *
   * @param className - Name of the case class
   * @param options   - Javascript object contains the flowType, and containerName information
   * in the creation of the work object.
   * @param options.flowType      Flow type
   * @param options.containerName Name of the containter
   * @param options.openCaseViewAfterCreate - The flag that determines if we can auto-navigate to new case view
   * or stay on the same page upon case creation.
   * Note:
   * - The default value is true.
   * - Set openCaseViewAfterCreate to true if we can auto-navigate to the new case view upon case creation.
   * - Set openCaseViewAfterCreate to false if we shouldn't navigate to the new case view and stay on the same page upon case creation.
   * @param options.modalOptions - JavaScript object that contains the properties required to customize the modal display.
   * @param options.modalOptions.dockable - The flag that determines where the modal is positioned in the screen.
   * Note:
   * - The default value is false
   * - Set dockable to true if the modal should be displayed at the bottom right corner of the screen.
   * - Set dockable to false if the modal should be displayed at the center of the screen.
   *
   * <br><br>
   * @param options.startingFields object containing fields to be set while creating a case
   * @param options.channelName Channel name
   * @param options.remoteCaseMeta The JSON object that contains the metadata for creating a traditional remote case.
   *
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  createWork(
    className: string,
    options: {
      flowType?: string;
      containerName?: string;
      openCaseViewAfterCreate?: boolean;
      modalOptions?: { dockable: boolean };
      startingFields?: object;
      channelName?: string;
      remoteCaseMeta?: RemoteCaseMeta;
    } = {
      flowType: '',
      containerName: '',
      openCaseViewAfterCreate: false,
      modalOptions: {
        dockable: false
      },
      startingFields: Object,
      remoteCaseMeta: {},
      channelName: ''
    }
  ) {
    if (!className) {
      return Promise.reject(new Error(this.MSG_CLASSNAME_REQUIRED));
    }

    if (typeof className !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CLASSNAME_STRING));
    }

    if (window.pms?.plugins?.c11n?.createWork) {
      // The PMC mobile app intercepts `createWork` action initiated from C11n-Web and handles it natively.
      return window.pms.plugins.c11n.createWork(className, 'pyStartCase', options);
    }

    const { dockable = false } = options?.modalOptions || {};
    const isExternalObjectCreationFlow = StateManager.getValue(
      '.isExternalObjectCreationFlow',
      'context_data',
      this.c11nEnv.getContextName()
    );
    let theType = getCreateWorkType(className, isExternalObjectCreationFlow as boolean);
    const actionConfig: ExtendedActionObject = ActionProcessor.buildActionObject(
      {
        class: className,
        flowType: 'pyStartCase',
        containerName: Constants.PRIMARY,
        openCaseViewAfterCreate: true,
        ...options,
        modalOptions: { isDockable: dockable }
      },
      this.c11nEnv
    );
    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    actionConfig.headers = { ...actionConfig.headers };
    if (options.channelName) {
      actionConfig.headers[Constants.X_ORIGIN_CHANNEL] = options.channelName;
    } else if (EnvironmentInfo.isWSSPortal()) {
      actionConfig.headers[Constants.X_ORIGIN_CHANNEL] = 'WSS';
    }

    if (RemoteCase.isRemoteCase(className)) {
      const { RemoteClassName, RemoteSystemID, name } = RemoteCase.getRemoteCase(className);
      actionConfig.className = className;
      actionConfig.class = RemoteClassName;
      ActionManagerInstance.setRemoteCaseHeaderByContext(actionConfig, RemoteSystemID);
      if (RemoteCase.isTraditionalRemoteCase(className)) {
        const remoteCaseActionDetails: ExtendedActionObject = {
          className,
          name,
          action: actionType.CREATEWORK,
          remoteClassName: RemoteClassName
        };
        theType = ActionManagerInstance.setTraditionalRemoteCaseParams(actionConfig, remoteCaseActionDetails);
      }
    }

    if (Debugger.getPreviewMode()) {
      actionConfig.debugInfo = { debugId: Utils.generateUniqueID() };
      const debugHeaders = HeaderProcessor.getCoreHeadersFromContext(actionConfig?.context || '');
      const debugId = String(debugHeaders?.debugInfo?.[Constants.COREHEADER_APP_DEBUG_ID] || '');
      if (debugId && theType !== actionType.CREATE_WORK) {
        actionConfig.debugInfo = {
          debugId
        };
      }
    }

    StateManager.dispatch({
      type: theType,
      payload: { ...actionConfig }
    });

    // return the Promise associated with this action
    return promise;
  }

  /**
   *
   * Gives the available actions for  data object with given <strong>dataViewID</strong>.
   *
   * @example <caption>Example for getDataObjectActions()</caption>
   * Example usage -
   *  const getDataObjectActionsPromise = getPConnect().getActionsApi().getDataObjectActions("D_DocusignEnvelopeByAccountandDocumentId", {'accountId': 'account8987', 'documentId': 'doc1234'}); <br>
   * getDataObjectActionsPromise.then(() => {
   *   // getDataObjectActions success handling
   * }).catch(() => {
   *   // getDataObjectActions failure handling
   * });
   *
   * @param dataViewID - Name of the data page
   * @param dataViewParameters   - Javascript object contains the parameters that need to be passed for the datapage
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  getDataObjectActions(dataViewID: string, dataViewParameters: object) {
    if (!dataViewID) {
      return Promise.reject(new Error(this.MSG_DATAVIEWID_REQUIRED));
    }

    if (typeof dataViewID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_DATAVIEWID_STRING));
    }

    const theType = actionType.GETDATAOBJECTACTIONS;
    const actionConfig = ActionProcessor.buildActionObject(
      {
        dataViewID,
        dataViewParameters
      },
      this.c11nEnv
    );
    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    const { promise } = this.actionsApiPromise(theType, actionConfig);

    StateManager.dispatch({
      type: theType,
      payload: { ...actionConfig }
    });

    // return the Promise associated with this action
    return promise;
  }

  /**
   *
   * This API will merge the descendants of the case in the context which is provided<br>
   *
   * @example <caption>Example for getDataObjectActions()</caption>
   * Example usage - With the the below example, getDescendants API will merge in the context provided.
   * const context = getPConnect().getContextName()
   * const getDescendantsPromise = getPConnect().getActionsApi().getDescendants("ON8TTL-C11NGALL-WORK D-206014", context)
   * getDescendantsPromise.then(() => {
   *   // getDescendants success handling
   * }).catch(() => {
   *   // getDescendants failure handling
   * });
   *
   * <br>
   * @param {string} caseID - Id(pzInsKey value) of the case whose descendants must be updated in the store.
   * @param {string} context - (optional) The ID of the container item that provides the current context of the case <br>
   * For example: "app/primary_1/workarea_1"
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  getDescendants(caseID: string, context?: string): Promise<object> {
    if (!caseID) {
      return Promise.reject(new Error(this.MSG_CASE_ID_REQUIRED));
    }

    if (typeof context !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTEXT_STRING));
    }
    const currentContext = context || this.c11nEnv.getContextName();
    const theType = actionType.GET_DESCENDANTS;
    const actionConfig = ActionProcessor.buildActionObject(
      {
        caseID,
        currentContext
      },
      this.c11nEnv
    );
    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    const getDescendantsActionType = getDescendantsAction({
      ...actionConfig,
      context: currentContext
    });
    StateManager.dispatch(getDescendantsActionType);

    // return the Promise associated with this action
    return promise;
  }

  /**
   * Open the work object associated with the given workID
   * <br>
   *
   * @example <caption>Example for openWorkByHandle()</caption>
   * Example usage -
   * const openWorkPromise = getPConnect().getActionsApi().openWorkByHandle("OPB1HW-SPACE-WORK RA-10001", "OPB1HW-SpaceTra-Work-RequestApproval"); <br>
   * openWorkPromise.then(() => {
   *  // open work by handle success handling
   * }).catch(() => {
   *  // open work by handle failure handling
   * });
   *
   * // The success callback is called if the work object is opened successfully.
   *
   * @param workID The work object to be opened<br>
   * Example: "OPB1HW-SPACE-WORK RA-10001"<br><br>
   *
   * @param className Name of the case class<br>
   * Example: "OPB1HW-SpaceTra-Work-RequestApproval"
   * <br><br>
   * @param options The JavaScript object that contains the properties required for opening the work object. <br>
   * @param options.targetContainer The name of the container that the work object will be opened in.
   * @param options.channelName Channel name
   * @param options.remoteCaseMeta The JSON object that contains the metadata for opening a traditional remote case.
   *
   * <br><br>
   * @returns A promise associated with the action (and stored in ActionManager)
   *
   */
  openWorkByHandle(
    workID: string,
    className: string,
    options: {
      viewType?: string;
      targetContainer?: string;
      channelName?: string;
      remoteCaseMeta?: RemoteCaseMeta;
    } = {}
  ) {
    if (!workID) {
      return Promise.reject(new Error('workID is required parameter'));
    }

    if (typeof workID !== 'string') {
      return Promise.reject(new TypeError('workID must be a string'));
    }

    if (!className) {
      return Promise.reject(new Error(this.MSG_CLASSNAME_REQUIRED));
    }

    if (typeof className !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CLASSNAME_STRING));
    }

    if (window.pms?.plugins?.c11n?.openWorkByHandle) {
      // The PMC mobile app intercepts `openWorkByHandle` action initiated from C11n-Web and handles it natively.
      return window.pms.plugins.c11n.openWorkByHandle(workID, className);
    }

    const actionInfo = {
      insKey: workID,
      acTargetName: options.targetContainer || Constants.PRIMARY,
      containerName: options.targetContainer || Constants.PRIMARY
    };

    let theType = CaseUtils.isObjectCaseType(className) ? actionType.GET_OBJECT : actionType.OPEN_WORK_BY_HANDLE;

    const actionConfig: ExtendedActionObject = ActionProcessor.buildActionObject(actionInfo, this.c11nEnv);

    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    actionConfig.headers = { ...actionConfig.headers };
    if (options.channelName) {
      actionConfig.headers[Constants.X_ORIGIN_CHANNEL] = options.channelName;
    } else if (EnvironmentInfo.isWSSPortal()) {
      actionConfig.headers[Constants.X_ORIGIN_CHANNEL] = 'WSS';
    }
    actionConfig.className = className;

    if (RemoteCase.isRemoteCase(className)) {
      theType = this.setRemoteCaseParamsForOpenCase(actionConfig, className, options.remoteCaseMeta) ?? theType;
    }

    if (Debugger.getPreviewMode() && ![Constants.WORKAREA].includes(options.targetContainer as string)) {
      actionConfig.debugInfo = { debugId: Utils.generateUniqueID() };
    }

    StateManager.dispatch({
      type: theType,
      payload: { ...options, ...actionConfig }
    });

    // return the Promise associated with this action
    return promise;
  }

  /**
   * This method is to add necessary parameters for remote case openWorkByHandle action
   * @param actionConfig
   * @param className
   * @param remoteCaseMeta
   * @returns string
   * @private
   */
  setRemoteCaseParamsForOpenCase(
    actionConfig: ExtendedActionObject,
    className: string,
    remoteCaseMeta: RemoteCaseMeta = {}
  ): string | undefined {
    const { RemoteSystemID, RemoteClassName, name } = RemoteCase.getRemoteCase(className);
    ActionManagerInstance.setRemoteCaseHeaderByContext(actionConfig, RemoteSystemID);
    actionConfig.isRemoteCase = true;
    actionConfig.remoteCaseClass = className;
    if (RemoteCase.isTraditionalRemoteCase(className)) {
      actionConfig.remoteCaseMeta = remoteCaseMeta;
      const remoteCaseActionDetails: ExtendedActionObject = {
        className,
        name,
        action: actionType.OPENCASE,
        remoteClassName: RemoteClassName
      };
      return ActionManagerInstance.setTraditionalRemoteCaseParams(actionConfig, remoteCaseActionDetails);
    }

    return undefined;
  }

  /**
   * Open the given assignment and associated with the given container target
   * <br>
   * @example <caption>Example for openAssignment()</caption>
   * Example usage -
   * const openAssignmentPromise = getPConnect().getActionsApi().openAssignment(
   *      "ASSIGN-WORKLIST OPB1HW-SPACE-WORK RA-43001!REQUEST_FLOW_0",
   *      "OPB1HW-SpaceTra-Work-RequestApproval",
   *      { containerName: "primary" }
   *  ); <br>
   * openAssignmentPromise.then(() => {
   *  // open assignment success handling
   * }).catch(() => {
   *  // open assignment failure handling
   * });
   *
   * // The success callback is called if the assignment is opened successfully.
   *
   * @param assignmentID - The ID of the assignment to be opened.<br>
   * Example: "ASSIGN-WORKLIST OPB1HW-SPACE-WORK RA-43001!REQUEST_FLOW_0"
   * <br><br>
   * @param className - Name of the case class<br>
   * Example: "OPB1HW-SpaceTra-Work-RequestApproval"
   * <br><br>
   * @param options - Javascript object containing containerName <br>
   * @param options.containerName Name of the container
   * @param options.channelName Channel name
   * @param options.isActionFromToDoList (optional) isActionFromToDoList
   * @param options.isChild (optional) isChild
   * @param options.context (optional) context
   * @param options.viewType (optional) viewType
   * @param options.skipBrowserSemanticUrlUpdate (optional) skipBrowserSemanticUrlUpdate
   * @param options.remoteCaseMeta The JSON object that contains the metadata for opening a traditional remote case assignment.
   *
   * <br>
   * Example:<br>
   * { containerName: "primary" }
   * <br><br>
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  openAssignment(
    assignmentID: string,
    className: string,
    options: {
      isActionFromToDoList?: boolean;
      isChild?: boolean;
      context?: string;
      viewType?: string;
      skipBrowserSemanticUrlUpdate?: boolean;
      containerName: string;
      channelName?: string;
      remoteCaseMeta?: RemoteCaseMeta;
    } = {
      isActionFromToDoList: false,
      isChild: false,
      context: '',
      viewType: '',
      skipBrowserSemanticUrlUpdate: false,
      containerName: '',
      channelName: '',
      remoteCaseMeta: {}
    }
  ) {
    if (!assignmentID) {
      return Promise.reject(new Error('assignmentID is required parameter'));
    }

    if (typeof assignmentID !== 'string') {
      return Promise.reject(new TypeError('assignmentID must be a string'));
    }

    if (!className) {
      return Promise.reject(new Error(this.MSG_CLASSNAME_REQUIRED));
    }

    if (typeof className !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CLASSNAME_STRING));
    }

    if (window.pms?.plugins?.c11n?.openAssignment) {
      // The PMC mobile app intercepts `openAssignment` action initiated from C11n-Web and handles it natively.
      return window.pms.plugins.c11n.openAssignment(assignmentID, className);
    }

    const actionInfo = {
      assignmentID,
      doDeleteCaseSummary: true,
      containerName: options.containerName,
      isActionFromToDoList: options.isActionFromToDoList,
      isChild: false
    };

    if (options.isChild) {
      actionInfo.containerName = Constants.PRIMARY;
      actionInfo.isChild = options.isChild;
    }

    let theType = OPEN_ASSIGNMENT;
    const actionConfig: ExtendedActionObject = ActionProcessor.buildActionObject(actionInfo, this.c11nEnv);

    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    actionConfig.headers = actionConfig.headers || {};
    if (options.channelName) {
      actionConfig.headers[Constants.X_ORIGIN_CHANNEL] = options.channelName;
    } else if (EnvironmentInfo.isWSSPortal()) {
      actionConfig.headers[Constants.X_ORIGIN_CHANNEL] = 'WSS';
    }

    if (RemoteCase.isRemoteCase(className)) {
      const { RemoteClassName, RemoteSystemID, name } = RemoteCase.getRemoteCase(className);
      ActionManagerInstance.setRemoteCaseHeaderByContext(actionConfig, RemoteSystemID);
      if (RemoteCase.isTraditionalRemoteCase(className)) {
        actionConfig.remoteCaseMeta = options.remoteCaseMeta;
        const remoteCaseActionDetails: ExtendedActionObject = {
          className,
          name,
          action: actionType.OPENASSIGNMENT,
          remoteClassName: RemoteClassName
        };
        theType = ActionManagerInstance.setTraditionalRemoteCaseParams(actionConfig, remoteCaseActionDetails);
      }
    }

    actionConfig.className = className;
    actionConfig.activeItemCaseViewMode = this.c11nEnv.getValue('context_data.caseViewMode');

    if (Debugger.getPreviewMode() && ![Constants.WORKAREA].includes(options.containerName)) {
      actionConfig.debugInfo = { debugId: Utils.generateUniqueID() };
    }

    // ToDo: Remove targetContainerName in case-epic of openAssignment and use containerName
    const commonPayload = {
      context: actionConfig.context || options.context,
      containerName: actionConfig.containerName || options.containerName,
      targetContainerName: actionConfig.containerName || options.containerName,
      viewType: actionConfig.viewType || options.viewType,
      skipBrowserSemanticUrlUpdate: options.skipBrowserSemanticUrlUpdate
    };

    StateManager.dispatch({
      type: theType,
      payload: { ...actionConfig, ...commonPayload }
    });

    // return the Promise associated with this action
    return promise;
  }

  /**
   * Finish the assignment (from the component's <strong>caseData.ID</strong>) that is
   * associated with the given case (from the component's <strong>caseData.caseID</strong>) and
   * the given Flow Container ID.
   *
   * <br>
   * @example <caption>Example for finishAssignment()</caption>
   * Example usage -
   * const finishAssignmentPromise = getPConnect().getActionsApi().finishAssignment("app/primary_1/workarea_1"); <br>
   * finishAssignmentPromise.then(() => {
   *  // finish assignment success handling
   * }).catch(() => {
   *  // finish assignment failure handling
   * });
   *
   * // The success callback is called if the assignment is submitted successfully.
   *
   * @param containerItemID - Id of the container <br>
   * For example: "app/primary_1/workarea_1"
   * @param options - The Javascript object that contains optional properties that provide additional information for submitting the assignment
   * For example: "{"outcomeID": 'Book a Hotel'}"
   * @param options.outcomeID - The ID generated for an outcome of the assignment that must be submitted.
   * For example: "{"jsActionQueryParams": "{"skipRoboticAutomation": true}"}"
   * @param options.jsActionQueryParams - The jsActionQueryParams will be added to the fetch call to append parameters.
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  finishAssignment(
    containerItemID: string,
    options: {
      isTriggeredFromDifferentContext?: boolean;
      outcomeID: string;
      jsActionQueryParams?: JSActionQueryParams;
    } = {
      outcomeID: '',
      jsActionQueryParams: {}
    }
  ) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }
    const context = getDataContextName(containerItemID);
    let msgContext = context;
    if (context?.includes('/workarea')) {
      msgContext = context.substring(0, msgContext?.lastIndexOf('/workarea'));
    }
    // if form has active suggestion, don't proceed for submission.
    if (isFormHasSuggestions(context)) {
      const errorMsgArr = [
        {
          type: 'error',
          message: this.c11nEnv.getLocalizationService().getLocalizedMessage(this.MSG_ACTIVE_SUGGESTION_UNATTENDED)
        }
      ];
      addMessages({
        messages: errorMsgArr,
        context: msgContext,
        category: publicConstants.PAGE
      });

      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        type: publicConstants.SUBMISSION_FAILURE_TYPE.FORM_VALIDATION_ERROR
      });
    }

    const isTriggeredFromDifferentContext = options?.isTriggeredFromDifferentContext;
    const { caseID, assignments, localActionID, caseActions, caseInfo, businessID, caseTypeID, caseTypeName } =
      getContainerDataForFinishAssignment(containerItemID, this.c11nEnv, {
        isTriggeredFromDifferentContext
      });

    const isInCreateStage = isAssignmentInCreateStage(caseInfo);
    let isACaseWideAction = false;
    let data;
    if (caseActions) {
      const actionObj = caseActions.find((caseAction: { ID: string }) => caseAction.ID === localActionID);
      isACaseWideAction = actionObj?.type === 'Case';
    } else {
      data = StateManager.getDataObject(context);
    }
    let { assignmentID } = getContainerDataForFinishAssignment(containerItemID, this.c11nEnv, {
      isTriggeredFromDifferentContext
    });
    if (data?.context_data?.actionType) {
      isACaseWideAction = data.context_data.actionType === Constants.CASE;
      assignmentID = assignmentID || data.context_data.assignmentID;
    }

    const actionID = getActionIdForFinishAssignment(assignments, assignmentID, localActionID, caseInfo?.activeActionID);

    const [className, workID] = caseID.split(' ');

    const actionInfo: ActionInfo = {
      class: className,
      actionID,
      assignmentID,
      caseID,
      workID,
      containerName: this.c11nEnv.getContainerName(),
      containerItemID,
      isCaseWideAction: isACaseWideAction,
      doDeleteCaseSummary: true,
      isInCreateStage,
      businessID,
      caseTypeID,
      caseTypeName
    };

    if (containerItemID?.includes(Constants.MODAL)) {
      actionInfo.containerName = Constants.MODAL;
      actionInfo.isModalAction = true;
    }
    actionInfo.outcome = options.outcomeID;
    actionInfo.jsActionQueryParams = options.jsActionQueryParams;

    if (caseInfo?.isLocalAction) {
      actionInfo.viewType = SCOPE_TYPES.FORM;
    }
    const theType = getFinishAssignmentActionType(caseTypeID, caseInfo?.isObjectWideAction);

    const actionConfig = ActionProcessor.buildActionObject(actionInfo, this.c11nEnv);
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    actionConfig.isProcessAction =
      this.c11nEnv.getValue('isProcessAction', 'isProcessAction') ||
      StateManager.getDataObject(context).context_data.isProcessAction;

    actionConfig.headers ??= {};

    if (EnvironmentInfo.isWSSPortal()) {
      actionConfig.headers[Constants.X_ORIGIN_CHANNEL] = 'WSS';
    }

    ActionsSequencer.executeOrQueueDeferredAction({
      type: theType,
      payload: {
        ...actionConfig,
        context
      }
    });

    // return the Promise associated with this action
    return promise;
  }

  /**
   * Open the next urgent assignment for the user
   * <br>
   * @example <caption>Example for getNextWork()</caption>
   * Example usage -
   * const getNextWorkPromise = getPConnect().getActionsApi().getNextWork()
   *  <br>
   * getNextWorkPromise.then(() => {
   *  // open Next Work success handling
   * }).catch(() => {
   *  // open Next Work failure handling
   * });
   *
   * // The success callback is called if the next Work is opened successfully.
   *
   * <br>
   * Example:<br>
   * { containerName: "primary" }
   * <br><br>
   * @returns A promise associated with the action (and stored in ActionManager)
   */

  getNextWork() {
    if (EnvironmentInfo.getRenderingMode() === 'HYBRID') {
      PubSubUtils.publish(publicConstants.PUB_SUB_EVENTS.CASE_EVENTS.GET_NEXT_WORK, {
        userIdentifier: EnvironmentInfo.getOperatorIdentifier()
      });
      return Promise.resolve();
    }
    const theType = OPEN_ASSIGNMENT;
    const actionConfig = ActionProcessor.buildActionObject(
      {
        containerName: Constants.PRIMARY,
        isGetNextWork: true
      },
      this.c11nEnv
    );
    const { promise } = this.actionsApiPromise(theType, actionConfig);

    StateManager.dispatch({
      type: theType,
      payload: { ...actionConfig }
    });
    // return the Promise associated with this action
    return promise;
  }

  /**
   * Open's the stage associated with the case using the stageID.
   *
   * <br>
   * @example <caption>Example for openStage()</caption>
   * Example usage -
   * const openStagePromise = getPConnect().getActionsApi().openStage("PRIM3"); <br>
   * openStagePromise.then(() => {
   *  // open stage success handling
   * }).catch(() => {
   *  // open stage failure handling
   * });
   *
   * // The success callback is called if the assignment is submitted successfully.
   *
   * @param stageID - Id of the Stage <br>
   * For example: "PRIM3"
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  openStage(stageID: string) {
    if (!stageID) {
      return Promise.reject(new Error('stageID is required parameter for openStage'));
    }

    const theType = OPEN_ASSIGNMENT;
    const caseID = this.c11nEnv.getValue(publicConstants.CASE_INFO.CASE_INFO_ID);
    const headers = this.c11nEnv.getCaseSummary().headers;
    const actionConfig = ActionProcessor.buildActionObject(
      {
        containerName: this.c11nEnv.getContainerName(),
        isOpenStage: true,
        stageID
      },
      this.c11nEnv
    );
    const { promise } = this.actionsApiPromise(theType, actionConfig);

    const openAssignmentAction = {
      type: theType,
      payload: { ...actionConfig, caseID, headers }
    };

    StateManager.dispatch(openAssignmentAction);
    // return the Promise associated with this action
    return promise;
  }

  submitBulkAction(containerItemID: string | string[]) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    const theType = actionType.SUBMIT_BULK_ACTION;
    const cases = this.c11nEnv.getValue(publicConstants.CASE_INFO.CASES);
    const actionID = this.c11nEnv.getValue(publicConstants.CASE_INFO.ACTIVE_ACTION_ID);

    const actionInfo: ActionInfo = {
      containerName: this.c11nEnv.getContainerName(),
      containerItemID,
      isCaseWideAction: true,
      doDeleteCaseSummary: true,
      isInCreateStage: false
    };

    if (containerItemID && containerItemID.includes(Constants.MODAL)) {
      actionInfo.containerName = Constants.MODAL;
      actionInfo.isModalAction = true;
    }

    const actionConfig = ActionProcessor.buildActionObject(actionInfo, this.c11nEnv);
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    const context = getDataContextName(containerItemID);

    StateManager.dispatch({
      type: theType,
      payload: {
        ...actionConfig,
        context,
        cases,
        actionID,
        isFormValid: isFormValid(context),
        classID: this.c11nEnv.getCurrentClassID()
      }
    });

    // return the Promise associated with this action
    return promise;
  }

  submitBulkActionAsync(containerItemID: string | string[]) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    const theType = actionType.SUBMIT_BULK_ACTION_ASYNC;
    const cases = this.c11nEnv.getValue(publicConstants.CASE_INFO.CASES);
    const actionID = this.c11nEnv.getValue(publicConstants.CASE_INFO.ACTIVE_ACTION_ID);
    const containerName = containerItemID.includes(Constants.MODAL) ? Constants.MODAL : this.c11nEnv.getContainerName();

    const actionInfo: ActionInfo = {
      containerName,
      containerItemID,
      isCaseWideAction: true,
      doDeleteCaseSummary: true,
      isInCreateStage: false,
      isModalAction: containerItemID.includes(Constants.MODAL)
    };

    const actionConfig = ActionProcessor.buildActionObject(actionInfo, this.c11nEnv);
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    const context = getDataContextName(containerItemID);

    StateManager.dispatch({
      type: theType,
      payload: {
        ...actionConfig,
        context,
        cases,
        actionID,
        isFormValid: isFormValid(context),
        classID: this.c11nEnv.getCurrentClassID()
      }
    });

    // return the Promise associated with this action
    return promise;
  }

  openBulkAction(
    actionID: string,
    options: {
      caseTypeID?: string;
      type?: string;
      target?: string;
      name?: string;
      selectedList?: string[];
      runningMode?: 'sync' | 'async';
      callbacks?: {
        submit?: () => void;
        cancel?: () => void;
      } | null;
      containerName?: string;
      bulkContextID?: string;
      bulkContextType?: string;
      showProgress?: boolean;
      progressMessage?: string;
    }
  ) {
    if (!actionID) {
      return Promise.reject(new Error('actionID is required parameter for openBulkAction'));
    }

    if (typeof actionID !== 'string') {
      return Promise.reject(new TypeError('actionID must be a string openBulkAction'));
    }

    if (window.pms?.plugins?.c11n?.openBulkAction) {
      const { callbacks } = options;
      options.callbacks = null; // callbacks not supported directly by iOS bridge
      return window.pms.plugins.c11n.openBulkAction(actionID, options).then((result: string | undefined) => {
        if (result === 'cancel') {
          callbacks?.cancel?.();
        } else {
          callbacks?.submit?.();
        }
      });
    }

    const { selectedList, runningMode } = options;
    const localActionType = options.type;
    const actionInfo = {
      selectedList,
      vcName: options.target,
      actionName: options?.name,
      actionID,
      doDeleteCaseSummary: true,
      localActionType,
      runningMode,
      ...options
    };
    const theType = actionType.OPEN_BULK_ACTION;

    const actionConfig = ActionProcessor.buildActionObject(actionInfo, this.c11nEnv);

    const { promise } = this.actionsApiPromise(theType, actionConfig);

    StateManager.dispatch({
      type: theType,
      payload: { ...actionConfig }
    });
    // return the Promise associated with this action
    return promise;
  }

  cancelBulkAction(containerItemID: string) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    const containerManager = new ContainerManager(this.c11nEnv);
    return containerManager.removeContainerItem({
      containerItemID,
      skipReleaseLockRequest: true
    });
  }

  /**
   * This API performs the approval action for the approval step configured in a case.
   * <br>
   * @example <caption>Example for approveCase()</caption>
   * Example usage -
   * const approveCasePromise = getPConnect().getActionsApi().approveCase("app/primary_1/workarea_1"); <br>
   * approveCasePromise.then(() => {
   *  // approve case success handling
   * }).catch(() => {
   *  // approve case failure handling
   * });
   *
   * // The success callback is called if the approve case action is successfully.
   *
   * @param containerItemID - Id of the container <br>
   * For example: "app/primary_1/workarea_1"
   *
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  approveCase(containerItemID: string) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    StateManager.dispatch({
      type: SET_PROPERTY,
      payload: {
        reference: publicConstants.CASE_INFO.APPROVALFLAG,
        value: Constants.APPROVED,
        context: containerItemID
      }
    });

    return this.finishAssignment(containerItemID);
  }

  /**
   * This API performs the reject action for the approval step configured in a case.
   * <br>
   * @example <caption>Example for rejectCase()</caption>
   * Example usage -
   * const rejectCasePromise = getPConnect().getActionsApi().rejectCase("app/primary_1/workarea_1"); <br>
   * rejectCasePromise.then(() => {
   *  // reject case success handling
   * }).catch(() => {
   *  // reject case failure handling
   * });
   *
   *
   * // The success callback is called if the reject case action is successfully.
   *
   * @param containerItemID  Id of the container<br>
   * For example: "app/primary_7/workarea_1"
   *
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  rejectCase(containerItemID: string) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    StateManager.dispatch({
      type: SET_PROPERTY,
      payload: {
        reference: publicConstants.CASE_INFO.APPROVALFLAG,
        value: Constants.REJECTED,
        context: containerItemID
      }
    });

    return this.finishAssignment(containerItemID);
  }

  /**
   * This API performs the resolve duplicate action for the search duplicate cases automation step configured in a case.
   * <br>
   * @example <caption>Example for resolveDuplicateCase()</caption>
   * Example usage - In this example, the API performs the resolve duplicate action for the search duplicate cases automation
   * step configured in the app/primary_1/workarea_1 container.
   * const resolveDuplicateCasePromise = getPConnect().getActionsApi().resolveDuplicateCase("app/primary_1/workarea_1"); <br>
   * resolveDuplicateCasePromise.then(() => {
   *  // resolve duplicate case success handling
   * }).catch(() => {
   *  // resolve duplicate case failure handling
   * });
   *
   * // The success callback is called if the resolve duplicate case action is successful.
   *
   * @param containerItemID - The ID of the container item that provides the current context of the case <br>
   * For example: "app/primary_1/workarea_1"
   *
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  resolveDuplicateCase(containerItemID: string) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    const [, duplicateCaseID] = this.c11nEnv.getValue(publicConstants.CASE_INFO.DUPLICATECASEINSKEY).split(' ');
    const actionSetDuplicateCaseFlag = {
      type: SET_PROPERTY,
      payload: {
        reference: publicConstants.CASE_INFO.DUPLICATECASEFLAG,
        value: Constants.DUPLICATE,
        context: containerItemID
      }
    };
    const actionSetDuplicateCaseID = {
      type: SET_PROPERTY,
      payload: {
        reference: publicConstants.CASE_INFO.DUPLICATECASEID,
        value: duplicateCaseID,
        context: containerItemID
      }
    };
    StateManager.dispatch([actionSetDuplicateCaseFlag, actionSetDuplicateCaseID]);
    return this.finishAssignment(containerItemID);
  }

  /**
   * This API performs the ignore duplicate action for the search duplicate cases automation step configured in a case.
   * <br>
   * @example <caption>Example for ignoreDuplicateCase()</caption>
   * Example usage - In this example, the API performs the ignore duplicate action for the search duplicate
   * cases automation step configured in the app/primary_1/workarea_1 container
   * const ignoreDuplicateCasePromise = getPConnect().getActionsApi().ignoreDuplicateCase("app/primary_1/workarea_1"); <br>
   * ignoreDuplicateCasePromise.then(() => {
   *  // ignore duplicate case success handling
   * }).catch(() => {
   *  // ignore duplicate case failure handling
   * });
   *
   * // The success callback is called if the ignore duplicate case action is successful.
   *
   * @param containerItemID - The ID of the container item that provides the current context of the case <br>
   * For example: "app/primary_1/workarea_1"
   *
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  ignoreDuplicateCase(containerItemID: string) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    const actionSetDuplicateCaseFlag = {
      type: SET_PROPERTY,
      payload: {
        reference: publicConstants.CASE_INFO.DUPLICATECASEFLAG,
        value: Constants.IGNORE,
        context: containerItemID
      }
    };

    const actionSetDuplicateCaseID = {
      type: SET_PROPERTY,
      payload: {
        reference: publicConstants.CASE_INFO.DUPLICATECASEID,
        value: '',
        context: containerItemID
      }
    };
    const actionSetDuplicateKey = {
      type: SET_PROPERTY,
      payload: {
        reference: publicConstants.CASE_INFO.DUPLICATECASEINSKEY,
        value: '',
        context: containerItemID
      }
    };
    StateManager.dispatch([actionSetDuplicateCaseFlag, actionSetDuplicateCaseID, actionSetDuplicateKey]);

    return this.finishAssignment(containerItemID);
  }

  /**
   * Opens the local action configure as Case Wide, Stage Wide or Assignment level.
   *
   * <br>
   * @example <caption>Example for openLocalAction()</caption>
   * Example usage -
   * const localActionPromise = getPConnect().getActionsApi().openLocalAction("EditDetails", { type : "Case" }); <br>
   * localActionPromise.then(() => {
   *  // local action success handling
   * }).catch(() => {
   *  // local action failure handling
   * });
   *
   * // The success callback is called if the local action is launched successfully.
   *
   * @param actionID  - Id of the local action
   * Example: "EditDetails"
   * <br>
   * @param options - Javascript object containing caseID, assignmentID, actionTitle and type properties necessary for opening local action <br>
   *
   * @param options.target - Container name <br>
   * @param options.caseID - Id of the case pointing to pzInsKey value. Will be used if provided else will get it using current context <br>
   * @param options.assignKey - Id of the assignment. Will be used if provided else will get it using current context <br>
   * @param options.actionTitle - Title of the local action. Used as modal header title when local action is displayed in modal dialog <br>
   * @param options.containerName - is used to launch the local action in a modal or in workarea<br>
   * @param options.name - name of the local action<br>
   * @param options.type - Type of the local action, Type can be "Assignment" or "Case" or "Stage" or "Express"<br>
   * @param options.refreshConditions - refreshConditions should be an array of objects containing the field name and event.
   * Assignment, Stage and Case are the scopes that determine whether it is a Casewide local action or stagewide local action or assignment level local action. <br>
   * Express type is used when we need to call the local actions from the different context<br>
   * <br>
   * Example:<br>
   {
   *    "name": "Edit Details",
   *     "type": “stage,
   *     "containerName": "modal"
   * },
   * {
   *      "name": "Transfer assignment",
   *      "type": “Assignment,
   *      "containerName": "modal"
   *  },
   *  {
   *      "name": "Change stage",
   *      "type": “Case,
   *      "containerName": “workarea”
   *  },
   *  {
   *      "name": "Transfer assignment",
   *      "type": “express”,
   *      "containerName": “workarea”
   *      “assignKey”:”ASSIGN-WORKLIST ON8TTL-GALLERY-WORK D-206014!VEHICLEDETAILS_FLOW”
   *      “caseID”:”"ON8TTL-GALLERY-WORK D-206014”
   *      "refreshConditions": [{field: ".Prop1", event: "Changes"}]
   *  },
   * {
   *    caseID: "ORG-APP-WORK W-102",
   *    assignmentID: "",
   *    actionTitle: "Edit Details",
   *    type: "Assignment" / "Case" / "Stage" / "Express"
   * }
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  // eslint-disable-next-line sonarjs/cognitive-complexity
  openLocalAction(actionID: string, options: CaseActionOptions) {
    if (!actionID) {
      return Promise.reject(new Error(this.MSG_ACTION_ID_REQUIRED));
    }

    if (typeof actionID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_ACTION_ID_STRING));
    }

    const caseID = this.getCaseID(options);

    if (window.pms?.plugins?.c11n?.openLocalAction) {
      options.caseID = caseID;
      return invokePMCAction(window.pms.plugins.c11n.openLocalAction, actionID, options);
    }

    const assignmentID = options.assignKey || this.c11nEnv.getValue(publicConstants.CASE_INFO.ASSIGNMENT_ID);
    const localActionType = options.type;
    const actionInfo = {
      caseID,
      assignmentID,
      vcName: options.target,
      actionName: options?.name,
      assignKey: assignmentID,
      actionID,
      doDeleteCaseSummary: true,
      localActionType,
      ...options
    };

    const theType = getLocalActionType(options);

    const actionConfig = ActionProcessor.buildActionObject(actionInfo, this.c11nEnv);

    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object

    const { promise } = this.actionsApiPromise(theType, actionConfig);
    if (theType === actionType.OPEN_OBJECT_ACTION) {
      StateManager.dispatch({
        type: theType,
        payload: { ...actionConfig }
      });
      return promise;
    }

    // saveAssignment assignment
    const contextName = this.c11nEnv.getContextName();
    const childContainers = getChildContainers(contextName)[0];
    let continueExecution = false;
    const childContainerItemID = getActiveContainerItemName(childContainers);
    // Only use the child container's remote header when the action targets a different case than
    // the current context's own case (i.e., a child/service case). For actions on the current case
    // (e.g., TransferInteraction on interaction case), use only the current context's remote header.
    const currentContextCaseID = this.c11nEnv.getValue(Constants.getProperty(publicConstants.CASE_INFO.CASE_INFO_ID));
    const isActionOnChildCase = caseID !== currentContextCaseID;
    const remoteHeader =
      isActionOnChildCase && childContainerItemID
        ? RemoteCase.getRemoteHeaderByContext(childContainerItemID) || RemoteCase.getRemoteHeaderByContext(contextName)
        : RemoteCase.getRemoteHeaderByContext(contextName);
    ActionManagerInstance.setRemoteCaseHeaderByContext(actionConfig, remoteHeader);
    let containerItemID;
    if (!childContainers) {
      containerItemID = contextName;
      StateManager.dispatch({
        type: theType,
        payload: { ...actionConfig, contextName, continueExecution }
      });
      return promise;
    }
    containerItemID = getActiveContainerItemName(childContainers);
    const containersData = getContainerData(childContainers);
    containersData?.accessedOrder.forEach((e: string | number) => {
      if (caseID === containersData.items[e].key) {
        continueExecution = true;
        containerItemID = containersData.items[e].context;
      }
    });

    // If the case is in review mode, then we skip saving the case
    if (
      !continueExecution ||
      (containerItemID &&
        StateManager.getStore()?.getState()?.data[containerItemID]?.context_data?.caseViewMode === 'review')
    ) {
      StateManager.dispatch({
        type: theType,
        payload: { ...actionConfig, contextName, continueExecution }
      });
      // return the Promise associated with this action
      return promise;
    }
    const context = containerItemID;
    const isModalAction = context && context.includes(Constants.MODAL);
    const saveAssignmentID = options.assignKey || this.c11nEnv.getValue(publicConstants.CASE_INFO.ASSIGNMENT_ID);
    const containerData = StateManager.getDataObject(containerItemID);
    const saveActionID = containerData?.caseInfo?.assignments
      ? containerData.caseInfo.assignments[0]?.actions[0]?.ID
      : undefined;
    const containerName = this.c11nEnv.getContainerName();
    const caseType = this.c11nEnv.getValue(publicConstants.CASE_INFO.CASE_TYPE_ID);
    const isFormValidFlag = true;
    const bIsProcessAction = this.c11nEnv.getValue('isProcessAction', 'isProcessAction');
    const saveActionPayload = ActionProcessor.buildActionObject(
      {
        context,
        containerItemID,
        assignmentID: saveAssignmentID,
        actionID: saveActionID,
        caseID,
        containerName,
        caseType,
        isModalAction,
        isFormValid: isFormValidFlag,
        isProcessAction: bIsProcessAction
      },
      this.c11nEnv
    );
    saveActionPayload.context = containerItemID;

    // cancelAssignment payload
    const cancelContext = getDataContextName(containerItemID);
    const caseInfo = this.c11nEnv.getCaseSummary();
    const isInCreateStage = isAssignmentInCreateStage(caseInfo);
    const cancelActionPayload = ActionProcessor.buildActionObject(
      { caseID, containerItemID, cancelContext },
      this.c11nEnv
    );

    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    let payload = {};
    if (isModalAction) {
      cancelActionPayload.acTargetName = Constants.MODAL;
      cancelActionPayload.isInCreateStage = isInCreateStage;
      cancelActionPayload.isModalAction = isModalAction;
      payload = {
        ...cancelActionPayload
      };
    } else {
      payload = {
        ...cancelActionPayload,
        acName: getResolvedTargetName(this.c11nEnv.getContainerName(), cancelActionPayload.context)
      };
    }

    StateManager.dispatch({
      type: theType,
      payload: {
        ...actionConfig,
        contextName,
        saveActionPayload,
        cancelActionPayload: payload,
        continueExecution
      }
    });
    // return the Promise associated with this action
    return promise;
  }

  /**
   * Opens the process action that is configured.
   * <br>
   * @example <caption>Example for openProcessAction()</caption>
   * Example usage -
   * const processActionPromise = getPConnect().getActionsApi().openProcessAction("VendorAddition_Flow", { type: "Case" }); <br>
   * processActionPromise.then(() => {
   *  // open process action success handling
   * }).catch(() => {
   *  // open process action failure handling
   * });
   *
   * // The success callback is called if the process action is launched successfully.
   *
   * @param actionID - Action Id <br>
   * Example: "VendorAddition_Flow"
   * <br>
   * @param options - Javascript object containing container target in which the process action will be shown <br>
   * @param options.caseID - Id of the case pointing to pzInsKey value. Will be used if provided else will get it using current context <br>
   * @param options.actionTitle - Title of the local action. Used as modal header title when local action is displayed in modal dialog <br>
   * @param options.type - Type of the process action. Type can be "Case" or "Stage" <br>
   *
   * <br>
   * Example:<br>
   * {
   *    caseID: "ORG-APP-WORK W-102",
   *    actionTitle: "Vendor Addition",
   *    type: "Case" / "Stage"
   * }
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  openProcessAction(actionID: string, options: CaseActionOptions) {
    if (!actionID) {
      return Promise.reject(new Error(this.MSG_ACTION_ID_REQUIRED));
    }

    if (typeof actionID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_ACTION_ID_STRING));
    }

    const caseID = this.c11nEnv.getValue(publicConstants.CASE_INFO.CASE_INFO_ID) || options.caseID;

    if (window.pms?.plugins?.c11n?.openProcessAction) {
      options.caseID = caseID;
      return invokePMCAction(window.pms.plugins.c11n.openProcessAction, actionID, options);
    }

    // ToDo: assignmentID might not be required for this action. Check and remove
    const assignmentID = this.c11nEnv.getValue(publicConstants.CASE_INFO.ASSIGNMENT_ID) || options.assignKey;
    const processActionType = options.type;
    const actionInfo = {
      caseID,
      assignmentID,
      vcName: options.target,
      actionName: options?.name,
      assignKey: assignmentID,
      actionID,
      doDeleteCaseSummary: true,
      processActionType,
      isProcessAction: true,
      ...options
    };
    const theType = OPEN_PROCESS_ACTION;

    const actionConfig = ActionProcessor.buildActionObject(actionInfo, this.c11nEnv);

    const contextName = this.c11nEnv.getContextName();
    const childContainers = getChildContainers(contextName)[0];
    let remoteHeader = RemoteCase.getRemoteHeaderByContext(contextName);
    // If RemoteSystemID not found at current context, check if the target case
    // belongs to a child container (remote service case). Only use the child container's
    // remote header when the caseID matches, to avoid routing interaction-level
    // process actions (e.g., Wrap up) to the remote server.
    if (!remoteHeader && childContainers) {
      const containersData = getContainerData(childContainers);
      containersData?.accessedOrder.forEach((e: string | number) => {
        if ((options.caseID || caseID) === containersData.items[e].key) {
          remoteHeader = RemoteCase.getRemoteHeaderByContext(containersData.items[e].context);
        }
      });
    }
    ActionManagerInstance.setRemoteCaseHeaderByContext(actionConfig, remoteHeader);

    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    const { promise } = this.actionsApiPromise(theType, actionConfig);

    StateManager.dispatch({
      type: theType,
      payload: { ...actionConfig }
    });
    // return the Promise associated with this action
    return promise;
  }

  /**
   * Navigate to the given step in the context of the given container
   * <br>
   *
   * @example <caption>Example for navigateToStep()</caption>
   * Example usage -
   * navigateToStepPromise = getPConnect().getActionsApi().navigateToStep("Step1", "app/primary_1/workarea_1"); <br>
   * navigateToStepPromise.then(() = > {
   *  // navigate to step success handling
   * }).catch(() => {
   *  // navigate to step failure handling
   * });
   *
   * // The success callback is called if the action is successful.
   *
   * @param stepID - ID of the step or "previous" to go to previous step <br>
   * For example: "Step1", "previous"
   * @param containerItemID - Id of the container item <br>
   * For example: "app/primary_1/workarea_1"
   *
   *
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  navigateToStep(stepID: string, containerItemID: string) {
    if (!stepID) {
      return Promise.reject(new Error('stepID is required parameter'));
    }

    if (typeof stepID !== 'string') {
      return Promise.reject(new TypeError('stepID must be a string'));
    }

    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    const theType = actionType.NAVIGATE_TO_STEP;

    const assignment = this.c11nEnv.getValue(publicConstants.NEXT_ASSIGNMENT_INFO_ID)
      ? this.c11nEnv.getValue(publicConstants.NEXT_ASSIGNMENT_INFO_ID)
      : this.c11nEnv.getValue(publicConstants.CASE_INFO.ASSIGNMENT_ID);
    const isInCreateStage = isAssignmentInCreateStage(this.c11nEnv.getCaseSummary());
    const caseID = this.c11nEnv.getValue(publicConstants.CASE_INFO.CASE_INFO_ID);
    const actionInfo = {
      ID: assignment,
      stepID,
      acTargetName: this.c11nEnv.getContainerName(),
      targetContainerName: this.c11nEnv.getContainerName(),
      containerItemID,
      doDeleteCaseSummary: true,
      isInCreateStage,
      caseID
    };

    if (containerItemID && containerItemID.includes(Constants.MODAL)) {
      actionInfo.acTargetName = Constants.MODAL;
    }

    const actionConfig = ActionProcessor.buildActionObject(actionInfo, this.c11nEnv);
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    const context = getDataContextName(containerItemID);

    StateManager.dispatch({
      type: theType,
      payload: {
        ...actionConfig,
        containerItemID,
        context
      }
    });

    return promise;
  }

  /**
   * Cancel the assignment that is associated with the current component's
   * caseID (from <strong>caseData.caseID</strong>).
   *
   * <br><br>
   * This action will also perform necessary actions related to the cancellation (e.g., release any locks). <br><br>
   *
   * @example <caption>Example for cancelAssignment()</caption>
   * Example usage -
   * const cancelAssignmentPromise = getPConnect().getActionsApi().cancelAssignment("app/primary_1/workarea_1"); <br>
   * cancelAssignmentPromise.then(() => {
   *  // cancel assignment success handling
   * }).catch(() => {
   *  // cancel assignment failure handling
   * });
   *
   * // The success callback is called if the API is executed successfully.
   *
   * @param containerItemID - Id of the container item <br>
   * For example: "app/primary_1/workarea_1"
   *
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  cancelAssignment(containerItemID: string, skipPublishCancelEvent: boolean) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    const { CASE_INFO } = publicConstants;
    const theType = actionType.CANCEL_ASSIGNMENT;
    const context = getDataContextName(containerItemID);
    const isModalAction = containerItemID && containerItemID.includes(Constants.MODAL);
    const caseInfo = this.c11nEnv.getCaseSummary();
    const isInCreateStage = isAssignmentInCreateStage(caseInfo);
    const caseID = this.c11nEnv.getValue(Constants.getProperty(CASE_INFO.CASE_INFO_ID));
    const assignmentID = this.c11nEnv.getValue(CASE_INFO.ASSIGNMENT_ID);
    const actionPayload = ActionProcessor.buildActionObject({ caseID, containerItemID, context }, this.c11nEnv);

    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    let payload = {};
    if (isModalAction) {
      actionPayload.acTargetName = Constants.MODAL;
      actionPayload.isInCreateStage = isInCreateStage;
      actionPayload.isModalAction = isModalAction;
      payload = {
        ...actionPayload
      };
    } else {
      payload = {
        ...actionPayload,
        acName: getResolvedTargetName(this.c11nEnv.getContainerName(), actionPayload.context)
      };
    }

    const { promise } = this.actionsApiPromise(theType, actionPayload);
    StateManager.dispatch({
      type: theType,
      payload
    });

    return promise.then(() => {
      if (!skipPublishCancelEvent) {
        publishCancelEvent({ caseID, assignmentID, context: this.c11nEnv.getContextName() });
      }
    });
  }

  /**
   * Cancel the local action that is associated with the current component's
   * caseID (from <strong>caseData.caseID</strong>).
   *
   * <br><br>
   *
   * There are different usecases the way this action works when the Edit Action and Local Actions are launched. <br>
   * <ol>
   * <li>
   * If Edit Action or local Action is launched, form is not dirty and action is fired from modal dialog.
   *     The case will be deleted by calling deleteCaseInCreateStage API.
   * </li>
   * <li>
   * If Edit Action or local Action is launched and the form is dirty and action is fired from modal dialog, then an event EVENT_SHOW_CANCEL_ALERT is dispatched.
   *     This EVENT_CANCEL_ALERT is used to show an alert dialog. An alert dialog with a message and 2 buttons is shown.
   *     (Go back and Discard) is shown.
   * </li>
   * </ul>
   *
   * @example <caption>Example for cancelLocalAction()</caption>
   * Example usage -
   * const cancelLocalAction = getPConnect().getActionsApi().cancelLocalAction("app/modal_3"); <br>
   * cancelLocalAction.then(() => {
   *  // cancel create stage assignment success handling
   * }).catch(() => {
   *  // cancel create stage assignment failure handling
   * });
   *
   * // The success callback is called if the API is executed successfully.
   *
   * @param containerItemID - Id of the container item <br>
   * <br><br>
   *
   * For example: "app/modal_3"
   *
   * @returns A promise associated with the action (and stored in ActionManager)
   */

  cancelLocalAction(containerItemID: string): Promise<object> | object {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    const { CASE_INFO } = publicConstants;
    const context = containerItemID;
    const theType = actionType.SHOW_CANCEL_ALERT;
    const isModalAction = containerItemID && containerItemID.includes(Constants.MODAL);
    const caseID = this.c11nEnv.getValue(CASE_INFO.CASE_INFO_ID);
    const actionPayload = ActionProcessor.buildActionObject({ caseID, containerItemID, context }, this.c11nEnv);
    const isFormContextDirty = isContextDirty(context);

    if (!isFormContextDirty) {
      const containerManager = new ContainerManager(this.c11nEnv);
      const removeContainerItemPromise = containerManager.removeContainerItem({ containerItemID });
      return removeContainerItemPromise.then(() => {
        publishCancelEvent({ caseID, context: this.c11nEnv.getContextName() });
      });
    }

    StateManager.dispatch({
      type: theType,
      payload: { ...actionPayload, isModalAction }
    });

    const { promise } = this.actionsApiPromise(theType, actionPayload);
    return promise;
  }

  /**
   * Cancel the assignment that is associated with the current component's
   * caseID (from <strong>caseData.caseID</strong>).
   *
   * <br><br>
   *
   * There are different usecases the way this action works when the assignment is in create stage. <br>
   * <ol>
   * <li>
   * If the current assignment is first assignment in create stage, form is not dirty and action is fired from modal dialog.
   *     The case will be deleted by calling deleteCaseInCreateStage API.
   * </li>
   * <li>
   * If the current assignment is in create stage and the form is dirty and action is fired from modal dialog, then an event EVENT_SHOW_CANCEL_ALERT is dispatched.
   *     This EVENT_SHOW_CANCEL_ALERT is used to show an alert dialog. An alert dialog with a message and 2 buttons is shown.
   *     (Go back and Discard) is shown.
   * </li>
   * </ul>
   *
   * @example <caption>Example for cancelCreateStageAssignment()</caption>
   * Example usage -
   * const cancelCreateStageAssignmentPromise = getPConnect().getActionsApi().cancelCreateStageAssignment("app/modal_3"); <br>
   * cancelCreateStageAssignmentPromise.then(() => {
   *  // cancel create stage assignment success handling
   * }).catch(() => {
   *  // cancel create stage assignment failure handling
   * });
   *
   * // The success callback is called if the API is executed successfully.
   *
   * @param containerItemID - Id of the container item <br>
   * <br><br>
   *
   * For example: "app/modal_3"
   *
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  cancelCreateStageAssignment(containerItemID: string) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    const { CASE_INFO } = publicConstants;
    const theType = actionType.SHOW_CANCEL_ALERT;
    const context = containerItemID;
    const isModalAction = context && context.includes(Constants.MODAL);
    const caseInfo = this.c11nEnv.getCaseSummary();
    const caseTypeID = this.c11nEnv.getValue(Constants.getProperty(Constants.CASE_INFO.CASE_TYPE_ID));
    const isInCreateStage = isAssignmentInCreateStage(caseInfo);
    const caseID = this.c11nEnv.getValue(Constants.getProperty(CASE_INFO.CASE_INFO_ID));
    const actionPayload = ActionProcessor.buildActionObject({ caseID, containerItemID, context }, this.c11nEnv);
    const bIsProcessAction = this.c11nEnv.getValue('isProcessAction', 'isProcessAction');
    const isCoexistenceUIKITApp = EnvironmentInfo.isCoexistenceAppOfType(Constants.APP_TYPE.UIKIT);
    actionPayload.isProcessAction = bIsProcessAction;
    // Local action with assignment in create stage return isInCreateStage. So for only for create stage assignments alert should be shown. Added check for not local action.
    if (isInCreateStage && (isModalAction || isCoexistenceUIKITApp) && !isLocalAction(caseInfo)) {
      const isFirstAssignment = this.c11nEnv.getValue(Constants.getProperty(Constants.CASE_INFO.IS_FIRST_ASSIGNMENT));
      const isFormContextDirty = isContextDirty(context);
      // If the assignment is in create stage, first assignment and context is not dirty. Then delete the case directly.
      if (this.shouldDeleteCaseInCreateStage(isFormContextDirty, isFirstAssignment, isCoexistenceUIKITApp)) {
        const ignoreCaseDeletion = Boolean(CaseUtils.isExternalObject(caseTypeID)) === true;
        return this.deleteCaseInCreateStage(containerItemID, ignoreCaseDeletion);
      }

      StateManager.dispatch({
        type: theType,
        payload: {
          ...actionPayload,
          isModalAction,
          isChildCase: isChildCase(caseInfo),
          isInCreateStage
        }
      });
    }

    const { promise } = this.actionsApiPromise(theType, actionPayload);
    return promise.then(() => {});
  }

  /**
   * Saves the opened assignment.
   *
   * @example <caption>Usage example</caption>
   * In this example, the success callback is called if the opened assignment is saved successfully.
   *
   * const saveAssignmentPromise = getPConnect().getActionsApi().saveAssignment("app/modal_1");
   * saveAssignmentPromise.then(() => {
   *  // saveAssignment success handling
   * }).catch(() => {
   *  // saveAssignment failure handling
   * });
   *
   * <br>
   *
   * @param containerItemID - The ID of the container item that contains information about the opened assignment. <br>
   * <br><br>
   * Example: "app/modal_1"
   * @returns A Promise associated with the action.
   */
  saveAssignment(containerItemID: string) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }
    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }
    const actionName = SAVE_AND_CLOSE;
    const actionPayload = this.getSaveActionPayload(containerItemID, true);
    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    const { promise } = this.actionsApiPromise(actionName, actionPayload);

    ActionsSequencer.executeOrQueueDeferredAction(saveAndCloseAction(actionPayload));

    // return the Promise associated with this action
    return promise;
  }

  /**
   * Save the current work object opened in the modal dialog.
   *
   * @example <caption>Example for saveAndClose()</caption>
   * Example usage -
   * const saveAndClosePromise = getPConnect().getActionsApi().saveAndClose("app/modal_1"); <br>
   * saveAndClosePromise.then(() => {
   *  // save and close success handling
   * }).catch(() => {
   *  // save and close failure handling
   * });
   *
   * // The success callback is called if the case is saved successfully.
   *
   * @param containerItemID - Id of the container item <br>
   * <br><br>
   * For example: "app/modal_1"
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  saveAndClose(containerItemID: string) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    const actionName = SAVE_AND_CLOSE;
    const actionPayload = this.getSaveActionPayload(containerItemID);
    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    const { promise } = this.actionsApiPromise(actionName, actionPayload);

    StateManager.dispatch(saveAndCloseAction(actionPayload));

    // return the Promise associated with this action
    return promise;
  }

  /**
   * Deletes the case that is currently in the create stage.
   * <br>
   * @example <caption>Usage example</caption>
   * In this example, the success callback is called if the case that is currently in the create stage is deleted successfully.
   *
   * const deleteCaseInCreateStagePromise = getPConnect().getActionsApi().deleteCaseInCreateStage("app/modal_1"); <br>
   * deleteCaseInCreateStagePromise.then(() => {
   *  // delete case in create stage success handling
   * }).catch(() => {
   *  // delete case in create stage success handling
   * });
   *
   * @param containerItemID - The ID of the container item that contains information about the case that is currently in the create stage.
   * <br><br>
   * Example: "app/modal_1"
   *
   * @param ignoreCaseDeletion - The flag that determines if a case will be deleted in the create stage.
   * <br>
   * The default value is false.
   * Set ignoreCaseDeletion to true if the case should not be deleted in the create stage
   * Set ignoreCaseDeletion to false if the case should be deleted in the create stage.
   *
   * @returns A Promise associated with the action.
   */
  deleteCaseInCreateStage(containerItemID: string, ignoreCaseDeletion: boolean = false) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    const actionName = DELETE_CASE_IN_CREATE_STAGE;
    const context = containerItemID;
    const isModalAction = context && context.includes(Constants.MODAL);
    const caseID = this.c11nEnv.getValue(Constants.getProperty(publicConstants.CASE_INFO.CASE_INFO_ID));
    const caseType = this.c11nEnv.getValue(Constants.getProperty(publicConstants.CASE_INFO.CASE_TYPE_ID));
    const actionPayload = ActionProcessor.buildActionObject(
      { caseID, caseType, containerItemID, context, ignoreCaseDeletion },
      this.c11nEnv
    );
    const bIsProcessAction = this.c11nEnv.getValue('isProcessAction', 'isProcessAction');
    const parentCaseInfo = this.c11nEnv.getValue(Constants.getProperty(publicConstants.CASE_INFO.PARENTCASEINFO));
    actionPayload.isProcessAction = bIsProcessAction;
    actionPayload.parentCaseInfo = parentCaseInfo;

    const primaryTarget = `${Constants.APP.APP}/${CONTAINER_NAMES.PRIMARY}`;
    const primaryContext = getContext(primaryTarget, CONTAINER_NAMES.PRIMARY);
    actionPayload.parentContext = primaryContext;
    if (isModalAction) {
      actionPayload.targetName = Constants.MODAL;
      actionPayload.containerName = CONTAINER_NAMES.MODAL;
      actionPayload.isModalAction = isModalAction;
    }

    StateManager.dispatch(deleteCaseInCreateStageAction(actionPayload));

    const { promise } = this.actionsApiPromise(actionName, actionPayload);
    return promise.then(() => {
      publishCancelEvent({ caseID, context: this.c11nEnv.getContextName() });
    });
  }

  /**
   * Fetches the recent items
   * <br>
   * @example <caption>Example for getRecents()</caption>
   * Example usage -
   * const recentsPromise = getPConnect().getActionsApi().getRecents(15); <br>
   * recentsPromise.then((data) => {
   *  // recents success handling
   * }).catch(() => {
   *  // recents failure handling
   * });
   *
   * // The success callback is called if the API is successful.
   *
   * @param maxResultsToFetch - Maximum number of results to be fetch <br>
   * <br><br>
   * For example: 15
   *
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  getRecents(maxResultsToFetch: number): Promise<object> {
    if (!maxResultsToFetch) {
      return Promise.reject(new Error('maxResultsToFetch is required parameter'));
    }

    if (typeof maxResultsToFetch !== 'number') {
      return Promise.reject(new TypeError('maxResultsToFetch must be number'));
    }

    let { restApi } = RouteParser.getRestEndPoint('getRecents') || {};
    restApi = `${restApi}?maxResultsToFetch=${maxResultsToFetch}`;

    const recentsURL = services.getServerURL() + restApi;
    return fetch(recentsURL, {
      method: 'GET',
      headers: {}
    });
  }

  /**
   * Update the recents with new item
   * <br>
   * @example <caption>Example for updateRecents(classID, caseID,label,ID)</caption>
   * Example usage -
   * const recentsUpdatePromise = getPConnect().getActionsApi().updateRecents("APP-1TC-Work-TCUser","T-99009","TCUser","APP-1TC-WORK T-99009"); <br>
   * recentsUpdatePromise.then((data) => {
   *  // update recents success handling
   * }).catch(() => {
   *  // update recents failure handling
   * });
   *
   * // The success callback is called if the API is successful.
   * @private
   * @param classID - Class of the Work Object <br>
   * @param caseID - id of the work object <br>
   * @param label - label of the work object <br>
   * @param ID - INs Key of the Work Object <br>
   *
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  updateRecents(classID: string, caseID: string, label: string, ID: string): Promise<object> {
    const { restApi } = RouteParser.getRestEndPoint('updateRecents') || {};
    const recentsURL = `${services.getServerURL()}${restApi}`;
    const updateRecentsData = { pyLabel: label, pyID: caseID, classID, ID };
    return fetch(recentsURL, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: updateRecentsData
    });
  }

  /**
   * Invoke a Logout REST API using fetch function of the service broker.
   *
   * @example <caption>Example for logout()</caption>
   * Example usage -
   * const logoutPromise = getPConnect().getActionsApi().logout(); <br>
   * logoutPromise.then(() => {
   *   // logout success handling
   * }).catch(() => {
   *   // logout failure handling
   * });
   *
   * //  the callbackFunction is called if the API successfully obtains a logout response.
   *
   * @returns A promise for the pre-processed logout response.
   * @private
   */
  logout(): Promise<object> {
    const { restApi } = RouteParser.getRestEndPoint('Logout') || {};
    const logoutUrl = `${services.getServerURL()}${restApi}`;
    return fetch(logoutUrl, {
      method: 'GET',
      headers: {}
    }).then((response: object) => {
      Object.keys(ContextTreeManager.rootNodes).forEach((context) => {
        GarbageCollector.cleanCaches(context);
      });
      ContextTreeManager.rootNodes = {};
      sessionStorage.clear();
      StateManager.resetStore();
      return Promise.resolve(response);
    });
  }

  /**
   * Invoke a REST API using fetch function of the service broker.
   *
   * @example <caption>Example for invoke()</caption>
   * Example usage -
   *
   * invokePromise = getPConnect().getActionsApi().invoke(url, options); <br>
   * invokePromise.then(() => {
   *   // invoke success handling
   * }).catch(() => {
   *   // invoke failure handling
   * });
   *
   * <br>
   * // invokes the URL with the options provided, and returns a response
   *
   * @param url - The URL for the request.<br><br>
   * Examples of <strong>url</strong> include:
   * <br>/prweb/api/v1/messages?filterFor=DATA-PORTAL $SpaceTra
   * <br>
   * <br>/prweb/api/v1/data/D_pxOperatorDetails?OperatorId=user%40gallery.com
   * @param opts - The HTTP methods that are used in the request body to fetch the REST API
   * @returns A promise for the pre-processed response.
   */
  invoke(url: string, opts: { method?: string; body?: object } = {}): Promise<object> {
    const actionPayload = {
      method: opts.method || 'GET',
      body: opts.body
    };
    return fetch(url, actionPayload, this.c11nEnv.getContextName());
  }

  /**
   * This API displays a preview of a case item inside the preview panel.
   *
   * @example <caption>Example for showCasePreview()</caption>
   * Example usage - getPConnect().getActionsApi().showCasePreview(pzInsKey, configObj);
   * // Show the preview of a case item(identified by unique key called pzInsKey) inside preview panel with the extra configuration object provided
   *
   * @param pzInsKey - Unique identifier of the case item.<br><br>
   * Examples of <strong>pzInsKey</strong> include:
   * <br>OPB1HW-SPACE-WORK 20RA-2
   * @param configObj object containing extra infomation like case class name which might be required.
   * @returns A promise associated with the action
   */
  showCasePreview(pzInsKey: string, configObj: object = {}) {
    this.loadView(pzInsKey, 'pyCasePreview', {
      container: CONTAINER_NAMES.PREVIEW,
      containerName: CONTAINER_NAMES.PREVIEW,
      context: publicConstants.APP.APP,
      ...configObj
    });
  }

  /**
   * This API displays a preview of a data item inside the preview panel.
   *
   * @example showDataPreview()
   * Show the preview of a data item under D_TestData with ProductId 'prd-1' inside preview panel with the extra configuration object provided
   * ```
   * getPConnect().getActionsApi().showDataPreview('D_TestData', {ProductId : 'prd-1'});
   * ```
   *
   * @param dataContext - Name of the data context<br><br>
   * Examples of <strong>dataContext</strong> include:
   * <br>D_TestData
   * @param dataContextParameters - Parameters required for the data context<br><br>
   * Examples of <strong>dataContextParameters</strong> include:
   * <br>{ProductId : 'prd-1'}
   * @param options - object containing additional infomation pertaining to opening of preview panel.
   * @returns A promise associated with the action
   */
  showDataPreview(dataContext: string, dataContextParameters: Record<string, unknown>, options?: PreviewOptions) {
    if (!dataContext) {
      return Promise.reject(new Error('dataContext is required parameter'));
    }

    if (typeof dataContext !== 'string') {
      return Promise.reject(new TypeError('dataContext must be string'));
    }

    if (!dataContextParameters) {
      return Promise.reject(new Error('dataContextParameters is required parameter'));
    }

    if (typeof dataContextParameters !== 'object') {
      return Promise.reject(new TypeError('dataContextParameters must be an object'));
    }

    return this.showData('pyDataPreview', dataContext, dataContextParameters, {
      container: CONTAINER_NAMES.PREVIEW,
      containerName: CONTAINER_NAMES.PREVIEW,
      context: publicConstants.APP.APP,
      ...(options || {}),
      skipSemanticUrl: true
    });
  }

  /**
   * This API loads a view with data from server based on the context of a case <br>
   *
   * @example <caption>Example for loadView()</caption>
   * In this example, the API loads the pyReview view with data from a server based on the context of the case whose ID is ON8TTL-GALLERY-WORK C-7001.
   * Example usage -
   * const loadViewPromise = getPConnect().getActionsApi().loadView(“ON8TTL-GALLERY-WORK C-7001”,”pyReview”); <br>
   * loadViewPromise.then(() => {
   *   // load view success handling
   * }).catch(() => {
   *   // load view failure handling
   * });
   *
   * @param {string} caseId Unique identifier of the case associated with a context
   * @param {string} viewName Name of the view which must be loadedwith data.
   * @param {object} config  - (Optional) Object to pass context and container information
   * @param {boolean} config.containerName - The view should be loaded on the provided container by creating the new context.
   * @param {boolean} config.context - The name of the context in which associated data of view should be merged.
   * @param {boolean} config.updateData - flag to decide whether associated data of view should be merged to the provided context. Default is false.
   * @param {readOnly} config.readOnly - If passed as true, the view will be rendered as readOnly
   * If updatedata passed as true along with context, The data of the associated view is merged in the provided context.
   * If updatedata passed as true without context, The data of the associated view is merged in the current context.
   * @returns {Promise<object>} A promise associated with the action (and stored in ActionManager)
   */
  loadView(caseId: string, viewName: string, config: LoadViewConfig): Promise<object> {
    let actionConfig = ActionProcessor.buildActionObject(
      {
        caseId,
        viewName,
        caseClassName: this.c11nEnv.getValue(Constants.getProperty(publicConstants.CASE_INFO.CASE_TYPE_ID)),
        RemoteSystemID: RemoteCase.getRemoteHeaderByContext(this.c11nEnv.getContextName())
      },
      this.c11nEnv
    );
    if (!config) {
      config = {
        containerName: this.c11nEnv.getContainerName(),
        updateData: false
      };
    }
    if (config && typeof config === 'object') {
      if (Object.hasOwn(config, 'container') && !Object.hasOwn(config, 'containerName')) {
        config.containerName = config.container;
      }
      actionConfig = {
        ...actionConfig,
        ...config
      };
    } else {
      actionConfig = {
        ...actionConfig,
        containerName: this.c11nEnv.getContainerName()
      };
    }

    const theType = actionType.LOAD_VIEW;

    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    const { promise } = this.actionsApiPromise(theType, actionConfig, false);

    StateManager.dispatch({
      type: theType,
      payload: { ...actionConfig }
    });

    // return the Promise associated with this action
    return promise;
  }

  /**
   *
   * Displays the page of a class.
   *
   * @example <caption>Example for showPage()</caption>
   * Example usage -
   * const showPagePromise = getPConnect().getActionsApi().showPage("pyHome", "Data-Portal"); <br>
   * showPagePromise.then(() => {
   *   // show page success handling
   * }).cath(() => {
   *   // show page failure handling
   * });
   *
   * // obtains and displays the view metadata and class name of a page.
   *
   * @param pageName - Name of the page
   * Some examples:<br><br>
   * page = "pyHome"<br>
   * @param className - Class name to which the page belongs to
   * Some examples:<br><br>
   * className: "Data-Portal"
   * <br><br>
   * @param options - Javascript object containing containerName <br>
   * @param options.containerName Name of the container
   * @param options.skipSemanticUrl - Flag to skip semantic url evaluation logic
   * <br>
   * Example:<br>
   * { containerName: "primary" }
   * <br><br>
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  showPage(
    pageName: string,
    className: string,
    options: { containerName: string; skipSemanticUrl: string } = {
      containerName: '',
      skipSemanticUrl: ''
    }
  ) {
    if (!pageName) {
      return Promise.reject(new Error(this.MSG_PAGENAME_REQUIRED));
    }

    if (typeof pageName !== 'string') {
      return Promise.reject(new TypeError(this.MSG_PAGENAME_STRING));
    }

    if (!className) {
      return Promise.reject(new Error(this.MSG_CLASSNAME_REQUIRED));
    }

    if (typeof className !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CLASSNAME_STRING));
    }

    if (window.pms?.plugins?.c11n?.showPage) {
      // The PMC mobile app intercepts `showPage` action initiated from C11n-Web and handles it natively.
      return window.pms.plugins.c11n.showPage(pageName, className, options);
    }

    const actionConfig = ActionProcessor.buildActionObject(
      {
        viewName: pageName,
        className
      },
      this.c11nEnv
    );
    const theType = actionType.SHOW_PAGE;

    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    const { promise } = this.actionsApiPromise(theType, actionConfig);

    if (Debugger.getPreviewMode()) {
      actionConfig.debugInfo = { debugId: Utils.generateUniqueID() };
    }

    StateManager.dispatch({
      type: theType,
      payload: {
        ...actionConfig,
        ...options,
        containerName: options.containerName || this.c11nEnv.getContainerName(),
        skipSemanticUrl: options.skipSemanticUrl
      }
    });

    // return the Promise associated with this action
    return promise;
  }

  /**
   * This API performs  loading the insight with semantic url.
   * <br>
   * @example <caption>Example for showInsight()</caption>
   * Example usage -
   * const showInsight = getPConnect().getActionsApi().showInsight("sample_insightID"); <br>
   * showInsight.then(() => {
   *  // show insight  success handling
   * }).catch(() => {
   *  // show insight  failure handling
   * });
   *
   * // The success callback is called if the  showInsight action is successfully.
   *
   * @param insightID - Id of the Insight <br>
   * For example: "d68f3f66-5ff3-443d-9ef9-544051fb74a4"
   *
   * @returns A promise associated with the action (and stored in ActionManager)
   * @function
   * @private
   */

  showInsight(insightID: string, options: { skipSemanticUrl?: boolean } = {}) {
    if (!insightID) {
      return Promise.reject(new Error(this.MSG_INSIGHTID_REQUIRED));
    }
    const actionConfig = ActionProcessor.buildActionObject({}, this.c11nEnv);
    const theType = actionType.SHOW_INSIGHT;

    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    const { promise } = this.actionsApiPromise(theType, actionConfig);

    StateManager.dispatch({
      type: theType,
      payload: {
        ...actionConfig,
        containerName: this.c11nEnv.getContainerName(),
        skipSemanticUrl: options.skipSemanticUrl || false,
        insightID
      }
    });

    // return the Promise associated with this action
    return promise;
  }

  /**
   * This API performs  loading the dashboard with semantic url.
   * <br>
   * @example <caption>Example for showDashboard()</caption>
   * Example usage -
   * const showDashboard = getPConnect().getActionsApi().showDashboard("sample_dashboardID"); <br>
   * showDashboard.then(() => {
   *  // show dashboard  success handling
   * }).catch(() => {
   *  // show dashboard  failure handling
   * });
   *
   * // The success callback is called if the  showDashboard action is successfully.
   *
   * @param dashboardID - Id of the dashboard <br>
   * For example: "d68f3f66-5ff3-443d-9ef9-544051fb74a4"
   *
   * @returns A promise associated with the action (and stored in ActionManager)
   * @function
   * @private
   */

  showDashboard(dashboardID: string, options: { skipSemanticUrl?: boolean } = {}) {
    if (!dashboardID) {
      return Promise.reject(new Error(this.MSG_DASHBOARDID_REQUIRED));
    }
    const actionConfig = ActionProcessor.buildActionObject({}, this.c11nEnv);
    const theType = actionType.SHOW_DASHBOARD;
    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    StateManager.dispatch({
      type: theType,
      payload: {
        ...actionConfig,
        containerName: this.c11nEnv.getContainerName(),
        skipSemanticUrl: options.skipSemanticUrl || false,
        dashboardID
      }
    });

    // return the Promise associated with this action
    return promise;
  }

  /**
   *
   * Displays the data of a page based on the data context.
   *
   * @example <caption>Example for showData()</caption>
   * Example usage -
   * const showDataPromise = getPConnect().getActionsApi().showData("EmployeeDetails", "D_EmployeeDetails", {pyGUID: "0759409f-4146-439c-aa25-57d4f495fee5"}); <br>
   * showDataPromise.then(() => {
   *   // show data success handling
   * }).cath(() => {
   *   // show data failure handling
   * });
   *
   *
   * @param pageName - The name of the view in which the data must be displayed.
   * @param dataContext - The name of page type data page whose data must be displayed.
   * @param dataContextParameters - The parameters associated with the data page.
   * @param options - The JavaScript object containing the properties to display the data.<br>
   * @param options.containerName - The name of the container that displays the data.
   * @param options.skipSemanticUrl - The flag that determines if the semantic URL evaluation logic must be skipped.
   * <br>
   * * Note:
   * - The default value is false
   * - If the value is false, the semantic URL evaluation logic will not be skipped.
   * - If the value is true, the semantic URL evaluation logic will be skipped.
   *
   * <br><br>
   * @returns A promise associated with the action (and stored in ActionManager)
   */
  showData(
    pageName: string,
    dataContext: string,
    dataContextParameters: object,
    options: {
      container?: string;
      context?: string;
      containerName?: string;
      skipSemanticUrl?: boolean;
    } = {
      containerName: '',
      skipSemanticUrl: false
    }
  ) {
    if (!pageName) {
      return Promise.reject(new Error(this.MSG_PAGENAME_REQUIRED));
    }

    if (typeof pageName !== 'string') {
      return Promise.reject(new TypeError(this.MSG_PAGENAME_STRING));
    }

    if (!dataContext) {
      return Promise.reject(new Error('dataContext is required parameter'));
    }

    if (typeof dataContext !== 'string') {
      return Promise.reject(new TypeError('dataContext must be string'));
    }

    const actionConfig = ActionProcessor.buildActionObject(
      {
        pageName,
        dataContext,
        dataContextParameters,
        RemoteSystemID: RemoteCase.getRemoteHeaderByContext(this.c11nEnv.getContextName())
      },
      this.c11nEnv
    );
    const theType = actionType.SHOW_DATA;

    if (!options?.containerName) {
      options.containerName =
        EnvironmentInfo.getRenderingMode() === 'HYBRID'
          ? RuntimeParamsAPI.getRuntimeParam(Constants.HYRBID_ROOT_CONTAINERNAME)
          : TargetMap.getTargetViewContainerName(theType);
    }

    // register this action with the ActionManager using
    //  actionMgrID that's been included in the action's payload
    //  and a promise. Note that buildActionObject gets an ActionMgrID
    //  and puts it in the returned object
    const { promise } = this.actionsApiPromise(theType, actionConfig);

    StateManager.dispatch({
      type: theType,
      payload: { ...actionConfig, ...options, skipSemanticUrl: Boolean(options.skipSemanticUrl) }
    });

    // return the Promise associated with this action
    return promise;
  }

  /**
   * Run a changeHandler for the given component context of the component through the Action Processor
   *
   * @example <caption>Example for changeHandler()</caption>
   * Example usage - getPConnect().getActionsApi().changeHandler(pConnObject, event);
   * // handles the change event triggered by the UI component.
   *
   * @param pConn PConnect object associated with handler being run
   * @param event The Document Object Model (DOM)event object associated with the change
   */
  changeHandler(pConn: C11nEnv, event: Event) {
    ActionProcessor.changeHandler(pConn, event);
  }

  /**
   * Run an eventHandler for the given component context of the component through the Action Processor
   *
   * @example <caption>Example for eventHandler()</caption>
   * Example usage - getPConnect().getActionsApi().eventHandler(pConnObject, event);
   * // handles the event triggered by the UI component.
   *
   * @param pConn PConnect object associated with handler being registered
   * @param event The Document Object Model (DOM)eventobject associated with the event.
   */
  eventHandler(pConn: C11nEnv, event: Event) {
    ActionProcessor.eventHandler(pConn, event);
  }

  /**
   * This API runs validations on the control and triggers FIELD_CHANGE event
   * so that ChangeObserver can listen for it.
   *
   * @example <caption>Example for triggerFieldChange()</caption>
   * Example usage - getPConnect().getActionsApi().triggerFieldChange(propName, value, skipValidation);
   * // the API validates and invokes the FIELD_CHANGE event as triggered by the specified control.
   *
   * @param propName name of the control on which the FIELD_CHANGE event must be invoked
   * @param value value of the control that will be validated and passed when invoking the FIELD_CHANGE event
   * @param skipValidation true/false value stating if client side validation needs to run or not
   * @param options - Javascript object containing skipDirtyValidation details.
   * @param options.skipDirtyValidation - flag to decide whether the property has to considered in Dirty Validation. Default is false. If passed as true Dirty Validation will be skipped on the respected property.
   */
  triggerFieldChange(
    propName: string,
    value: any,
    skipValidation: boolean = false,
    options: { skipDirtyValidation: boolean } = { skipDirtyValidation: false }
  ) {
    const context = this.c11nEnv.getContextName();
    const pageReference = this.c11nEnv.getPageReference();
    const reference = ContextProcessor.getContextPath(pageReference, propName);
    if (!skipValidation) {
      this.c11nEnv.getValidationApi().validate(value);
    }
    if (this.c11nEnv.isInsideList()) {
      this.c11nEnv.getListActions().updateProperty(propName, value, options);
    }
    let classID = this.c11nEnv.getValue(Constants.CLASS_ID);
    const propNameSplitArray = propName ? propName.split('.').filter((val) => !!val) : [];
    if (propNameSplitArray.length > 1) {
      propNameSplitArray.pop();
      classID = StateManager.getValue(`.${propNameSplitArray.join('.')}.classID`, pageReference, context);
    }
    propName = Utils.trimDotForProperty(propName);
    StateManager.dispatch({
      type: FIELD_CHANGE,
      payload: {
        reference,
        value,
        context,
        pageReference: this.c11nEnv.getPageReference(),
        propertyName: propName || this.c11nEnv.getComponentConfig().value,
        classID
      }
    });
  }

  /**
   * This API dispatches the SET_PROPERTY event on a specified control to store a specified value in the Redux Store.
   * This API also deletes the error messages of the control by calling the clearMessages API in the MessageManager module.
   *
   * @example <caption>Example for updateFieldValue()</caption>
   * Example usage - getPConnect().getActionsApi().updateFieldValue(propName, value);
   * // the API stores the value passed in the Redux Store as triggered by the control.
   *
   * @param propName name of the control whose value must be stored in the Redux Store
   * @param value value of the control to be stored in the Redux Store
   * @param options - Javascript object containing removePropertyFromChangedList, isArrayDeepMerge details
   * @param options.removePropertyFromChangedList - removePropertyFromChangedList - pass true to delete entry in changedPropertyList
   * @param options.isArrayDeepMerge - flag to decide whether to deep merge the objects or values inside array or not
   * @param options.skipDirtyValidation - flag to decide whether the property has to considered in Dirty Validation. Default is false. If passed as true Dirty Validation will be skipped on the respected property.
   * @param options.isListEntry - removePropertyFromChangedList - pass true to delete entry in the List
   */
  updateFieldValue(
    propName: string,
    value: any,
    options: {
      removePropertyFromChangedList?: boolean;
      isArrayDeepMerge?: boolean;
      skipDirtyValidation?: boolean;
      isListEntry?: boolean;
      contextPageReference?: string;
      associatedProperty?: string;
    } = {}
  ) {
    const {
      removePropertyFromChangedList = false,
      isArrayDeepMerge = true,
      contextPageReference = '',
      isListEntry = false,
      skipDirtyValidation = false,
      associatedProperty
    } = options;
    const context = this.c11nEnv.getContextName();
    let pageReference = this.c11nEnv.getPageReference();
    const dataRelationshipContext = contextPageReference?.startsWith(pageReference)
      ? contextPageReference.replace(pageReference, '')
      : contextPageReference;
    pageReference = contextPageReference || pageReference;
    const reference = ContextProcessor.getContextPath(pageReference, propName);
    let isStrictTracking = false;
    if (this.c11nEnv.isInsideList()) {
      this.c11nEnv
        .getListActions()
        .updateProperty(dataRelationshipContext ? `${dataRelationshipContext}${propName}` : propName, value, {
          isArrayDeepMerge,
          skipDirtyValidation
        });
    }
    if (this.c11nEnv.isBulkAction()) {
      isStrictTracking = true;
    }
    if (!propName) propName = this.c11nEnv.getStateProps().value;
    clearMessages({
      type: publicConstants.MESSAGES.MESSAGES_TYPE_ERROR,
      property: propName,
      pageReference,
      context,
      isListEntry
    });
    StateManager.dispatch({
      type: SET_PROPERTY,
      payload: {
        reference,
        value,
        context,
        isArrayDeepMerge,
        skipDirtyValidation,
        ...(isStrictTracking && { isStrictTracking }),
        ...(removePropertyFromChangedList && { removePropertyFromChangedList }),
        ...(associatedProperty && { associatedProperty: `${pageReference}${associatedProperty}` })
      }
    });
  }

  /**
   * Activates the modal container item of the passed container item id
   * <br>
   * @example <caption>Example for activateModalContainerItem()</caption>
   * Example usage - getPConnect().activateModalContainerItem({
   *   context: "app/primary_1",
   *   isModalAction: true
   * }).then(successCallback);
   *
   * // The success callback is called if the case is deleted successfully.
   *
   *
   * @param actionInfo - Javascript object containing container properties like context and isModalAction<br>
   * Example:<br>
   * {
   *   context: "app/primary_1",
   *   isModalAction: true
   * }
   *
   * @returns A promise associated with the action (and stored in ActionManager)
   *
   * @function
   * @private
   */
  activateModalContainerItem(actionInfo: { context: string }) {
    const actionName = actionType.ACTIVATE_MODAL_CONTAINER_ITEM;
    const { context } = actionInfo;
    const isModalAction = context && context.includes(Constants.MODAL);
    const actionPayload = ActionProcessor.buildActionObject(
      {
        ...actionInfo,
        containerName: this.c11nEnv.getContainerName(),
        containerItemID: context
      },
      this.c11nEnv
    );

    if (isModalAction) {
      actionPayload.containerName = Constants.MODAL;
      actionPayload.isModalAction = isModalAction;
    }

    StateManager.dispatch({
      type: actionName,
      payload: actionPayload
    });

    const { promise } = this.actionsApiPromise(actionName, actionPayload);
    return promise;
  }

  refreshCaseView(
    caseID: string,
    viewID: string,
    pageReference: string,
    options: ActionAPIOptions = {}
  ): Promise<RefreshCaseView> {
    const { promise, actionMgrID } = this.actionsApiPromise('REFRESH_DATA');

    const {
      autoDetectRefresh,
      propertyName,
      preserveClientChanges,
      classID,
      context,
      optionalPayload,
      isArrayDeepMerge
    } = options;
    const refreshOptions: RefreshAPIOptions = {
      actionMgrID,
      caseViewAction: true,
      preserveClientChanges,
      propertyName,
      classID,
      isArrayDeepMerge,
      optionalPayload
    };
    if (options.refreshFor) {
      refreshOptions.refreshFor = options?.refreshFor;
    }
    if (options.resetForm) {
      refreshOptions.resetForm = options?.resetForm;
    }
    const contextName = context || this.c11nEnv.getContextName();
    const reference = this.c11nEnv.getPageReference();
    const isMultiRecordData = this.c11nEnv.getValue(Constants.CONTEXT_DATA_MODAL_VIEW.IS_MULTI_RECORD_DATA);

    if (
      !isMultiRecordData &&
      autoDetectRefresh &&
      (isAssignment(contextName, reference) || isCaseWideAction(contextName, reference))
    ) {
      refreshAssignment(contextName, pageReference, refreshOptions);
    } else {
      dispatchCaseViewRefresh(caseID, viewID, contextName, pageReference, refreshOptions);
    }
    return promise as Promise<RefreshCaseView>;
  }

  refreshEmbeddedDataWithAction(
    caseID: string,
    assignmentID: string,
    actionID: string,
    interestPage: string,
    interestPageActionID: string,
    localAction: boolean,
    isMultiRecordData: boolean,
    editType: string,
    options: ActionAPIOptions = {}
  ): Promise<RefreshCaseView> {
    const { promise } = this.actionsApiPromise(refreshConstants.REFRESH_DATA);

    const contextName = this.c11nEnv.getContextName();
    dispatchRefreshForEmbeddeddataWithAction(
      caseID,
      assignmentID,
      actionID,
      contextName,
      interestPage || this.c11nEnv.getPageReference(),
      interestPageActionID,
      localAction,
      isMultiRecordData,
      editType,
      options
    );
    return promise as Promise<RefreshCaseView>;
  }
  /**
   * Displays the view to add/update a record in Embedded data type.
   * <br>
   *
   * @example <caption>Example for openEmbeddedDataModal()</caption>
   * In this example, the success callback is called if the modal is opened successfully
   * getPConnect().getActionsApi().openEmbeddedDataModal('Create', pConnObject, '.Employees', 1, 'CREATE', 'Add Employee').then(() => {
   *  // openEmbeddedDataModal success handling
   * }).catch(() => {
   *  // openEmbeddedDataModal failure handling
   * })
   *
   * @param viewName The name of the view
   * @param component PConnect object associated with the Embedded data table
   * @param targetProperty The property associated to Embedded data
   * @param index The index of the new row
   * @param action CREATE/EDIT action
   * @param heading Heading for the create/edit modal
   * @returns A Promise associated with the action.
   * <br><br>
   */

  openEmbeddedDataModal(
    viewName: string,
    component: Component,
    targetProperty: string,
    index: number | string,
    action: string,
    heading: string,
    editType: string,
    interestPageActionID: string
  ): Promise<object> {
    const theType = actionType.OPEN_EMBEDDED_DATA_MODAL;
    let caseID = component.getCaseInfo().getKey();
    const localAction = component.getValue(publicConstants.CASE_INFO.IS_LOCAL_ACTION);
    const assignmentID = component.getValue(publicConstants.CASE_INFO.ASSIGNMENT_ID);
    const actionID = getActionID(component, editType, localAction);
    const pageReference = component.getPageReference();
    let interestPage =
      typeof index === 'string'
        ? `${component
            .getPageReference()
            .replace(Constants.getProperty(publicConstants.CASE_INFO.CASE_INFO_CONTENT), '')}${targetProperty}.${index}`
        : `${component
            .getPageReference()
            .replace(
              Constants.getProperty(publicConstants.CASE_INFO.CASE_INFO_CONTENT),
              ''
            )}${targetProperty}[${index}]`;
    if (pageReference.startsWith(publicConstants.OBJECT_INFO.OBJECT_INFO) && !caseID) {
      caseID = component.getCaseInfo().getObjectKey();
      interestPage = `${component
        .getPageReference()
        .replace(publicConstants.OBJECT_INFO.OBJECT_INFO_CONTENT, '')}${targetProperty}[${index}]`;
    }

    const actionInfo = {
      targetProperty,
      index,
      viewID: viewName,
      ID: caseID,
      actionType: action,
      interestPage: Utils.convertPageRefToClipboardPageNotation(interestPage),
      heading,
      editType,
      interestPageActionID: editType === Constants.ACTION ? interestPageActionID : undefined,
      actionID,
      isLocalAction: localAction,
      operationFlag: Constants.EMBEDDED_DATA_MODAL.SHOW_ROW,
      isPageGroup: typeof index === 'string',
      assignmentID: !localAction && editType === Constants.ACTION ? assignmentID : undefined
    };

    const actionConfig = ActionProcessor.buildActionObject(actionInfo, this.c11nEnv);
    if (editType === Constants.ACTION) {
      const eTag = Utils.getETag(actionConfig.context);
      actionConfig.headers = { 'If-Match': eTag };
    }
    const { promise } = this.actionsApiPromise(theType, actionConfig);

    const featureMap: { granularUIRefresh: boolean } = EnvironmentInfo.getFeatureMap() as {
      granularUIRefresh: boolean;
    };

    if (featureMap.granularUIRefresh) {
      // get the columns
      const rawConfig = component.getMetadata();
      const columns = rawConfig.config.children[0].children || [];
      // iterate over the columns to get the field meta data
      columns.forEach(
        (column: {
          config: {
            value: string;
            classID: string;
          };
        }) => {
          // get the fieldmeta data
          const propertyName = AnnotationProcessor.trimAnnotation(column.config.value);
          const fieldMetadata = getPropertyMetadata(
            propertyName.startsWith('.') ? propertyName.replace('.', '') : propertyName,
            column.config.classID
          ) as PropertyModel;

          // if declaretarget is true then create a node in declare targets
          if (fieldMetadata?.isDeclarativeTarget) {
            // create a declaretarget node
            ContextTreeManager.addFieldNode(
              component.getContextName(),
              pageReference + interestPage,
              component.viewName,
              propertyName,
              '',
              {
                isDeclarativeTarget: true,
                propertyName,
                pageReference: pageReference + interestPage
              }
            );
          }
        }
      );
    }

    StateManager.dispatch({
      type: theType,
      payload: {
        ...actionConfig,
        containerName: publicConstants.MODAL,
        skipSemanticUrl: true
      }
    });
    return promise;
  }

  /**
   * Submits the view and add/update a record to Embedded data type.
   * <br>
   *
   * @example <caption>Example for submitEmbeddedDataModal()</caption>
   * In this example, the success callback is called if a row is added/edited successfully
   * getPConnect().getActionsApi().submitEmbeddedDataModal('app/modal_3').then(() => {
   *  // submitEmbeddedDataModal success handling
   * }).catch(() => {
   *  // submitEmbeddedDataModal failure handling
   * })
   *
   @param containerItemID - The ID of the container item containing the add/edit view of the Embedded data type.
   * @returns A Promise associated with the action.
   * <br><br>
   */

  submitEmbeddedDataModal(containerItemID: string) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    const theType = actionType.SUBMIT_EMBEDDED_DATA_MODAL;

    let ID = this.c11nEnv.getValue(Constants.getProperty(publicConstants.CASE_INFO.CASE_INFO_ID));
    const assignmentID = this.c11nEnv.getValue(Constants.getProperty(publicConstants.CASE_INFO.ASSIGNMENT_ID));
    let pageReference = this.c11nEnv
      .getPageReference()
      .replace(Constants.getProperty(publicConstants.CASE_INFO.CASE_INFO_CONTENT), '');
    const context = getDataContextName(containerItemID);
    const modalView = this.c11nEnv.getValue(
      Constants.getProperty(Constants.CONTEXT_DATA_MODAL_VIEW.CONTEXT_DATA_MODAL_VIEW)
    );
    const {
      interestPageActionID,
      editType,
      isLocalAction: localAction,
      actionID,
      viewName: viewID,
      parentContext
    } = modalView;

    if (pageReference.startsWith(publicConstants.OBJECT_INFO.OBJECT_INFO) && !ID) {
      ID = this.c11nEnv.getValue(publicConstants.OBJECT_INFO.OBJECT_INFO_ID);
      pageReference = this.c11nEnv.getPageReference().replace(publicConstants.OBJECT_INFO.OBJECT_INFO_CONTENT, '');
    }

    const actionInfo = {
      containerItemID,
      containerName: this.c11nEnv.getContainerName(),
      doDeleteCaseSummary: true,
      isInCreateStage: false,
      ID,
      viewID,
      isFormValid: isFormValid(context),
      interestPage: Utils.convertPageRefToClipboardPageNotation(pageReference),
      parentContext,
      isModalAction: false,
      target: this.c11nEnv.getTarget(),
      editType,
      actionID,
      operationFlag: Constants.EMBEDDED_DATA_MODAL.SUBMIT_ROW,
      interestPageActionID,
      isLocalAction: localAction,
      assignmentID
    };

    if (containerItemID && containerItemID.includes(Constants.MODAL)) {
      actionInfo.containerName = Constants.MODAL;
      actionInfo.isModalAction = true;
    }

    const actionConfig = ActionProcessor.buildActionObject(actionInfo, this.c11nEnv);
    if (editType === Constants.ACTION) {
      const eTag = Utils.getETag(actionConfig.context);
      actionConfig.headers = { 'If-Match': eTag };
    }
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    if (isContextDirty(context)) {
      const [pageNameWithoutPagelist, pageListName, rowIndex] = utils.getPageListNameAndIndexFromPageName(
        this.c11nEnv.getPageReference()
      );
      ContextTreeManager.mutatePageList(parentContext, pageNameWithoutPagelist, pageListName, {
        type: actionType.CLEAR_MESSAGES,
        rowIndex
      });
      StateManager.dispatch({
        type: actionType.CLEAR_MESSAGES,
        payload: {
          pageReference: this.c11nEnv.getPageReference(),
          context: parentContext,
          isListEntry: true
        }
      });
    }

    ActionsSequencer.executeOrQueueDeferredAction({
      type: theType,
      payload: {
        ...actionConfig,
        context
      }
    });
    return promise;
  }

  /**
   * Fill the assignment form for the user with logical sample data based on the name and type of field using AI
   * <br>
   * @example <caption>Example for fillFormWithAI()</caption>
   * Example usage -
   * const fillFormWithAIPromise = getPConnect().getActionsApi().fillForWithAI('app/primary_1/workarea_1')
   *  <br>
   * fillFormWithAIPromise.then(() => {
   *  // fillFormWithAI success handling
   * }).catch(() => {
   *  // fillFormWithAI failure handling
   * });
   *
   * // The success callback is called if the form is filled successfully using fillFormWithAI
   *
   * <br>
   * @param containerItemID - ID of the container <br>
   * For example: "app/primary_1/workarea_1"
   * <br><br>
   * @returns A promise associated with the action (and stored in ActionManager)
   */

  fillFormWithAI(containerItemID: string) {
    if (!containerItemID) {
      return Promise.reject(new Error(this.MSG_CONTAINERITEMID_REQUIRED));
    }

    if (typeof containerItemID !== 'string') {
      return Promise.reject(new TypeError(this.MSG_CONTAINERITEMID_STRING));
    }

    const contextName = getDataContextName(containerItemID);
    const pageReference = this.c11nEnv.getPageReference();

    const { promise, actionMgrID } = this.actionsApiPromise(actionType.FILL_FORM_WITH_AI);

    const refreshOptions = {
      actionMgrID,
      isFillFormWithAI: true,
      preserveClientChanges: false
    };

    refreshAssignment(contextName, pageReference, refreshOptions);

    return promise;
  }

  /**
   * Handles the subscript action for a given page group in the respective container item.
   *
   * @param {string} containerItemID - The ID of the container item.
   * @param {any} c11nEnv - The context object used for this instance of the ActionsApi.
   * @returns {Promise<any> | null} - A promise associated with the action or null if the subscript view is invalid.
   * @private
   */
  handleSubscript(containerItemID: string, c11nEnv: C11nEnv): Promise<any> | null {
    type ResolvedSubscriptView = { name: string } | object[]; // Define the possible types

    const resolvedSubscriptView = resolveView(
      publicConstants.VIEW_NAMES.SUBSCRIPT_CREATE_VIEW
    ) as ResolvedSubscriptView;

    if (!resolvedSubscriptView) {
      return Promise.reject(new Error(this.MSG_SUBSCRIPT_VIEW_REQUIRED));
    }

    const theType = actionType.SUBMIT_SUBSCRIPT;
    const actionPayload = ActionProcessor.buildActionObject({ containerItemID }, c11nEnv);

    // Dispatch action with necessary payload
    StateManager.dispatch({
      type: theType,
      payload: {
        ...actionPayload,
        isDataObject: true,
        skipReleaseLockRequest: true,
        isFormValid: isFormValid(containerItemID),
        context: containerItemID
      }
    });

    // Return promise from actions API
    const { promise } = this.actionsApiPromise(theType, actionPayload);
    return promise;
  }

  /**
   * Displays the create view of a subscript.
   *
   * @param {string} className - The name of the class that provides the type that the subscript should belong to.
   * @param {string} referenceListStr - The reference list string for the subscript.
   * @param {string} subscriptName - The name of the subscript.
   * @param {string} [viewName=publicConstants.VIEW_NAMES.SUBSCRIPT_CREATE_VIEW] - The name of the create view that is to be displayed for the subscript.
   * @returns {Promise<any>} - A promise associated with the action.
   * @private
   */
  showSubscriptCreateView(
    className: string,
    referenceListStr: string,
    subscriptName: string,
    viewName: string = publicConstants.VIEW_NAMES.SUBSCRIPT_CREATE_VIEW
  ) {
    const actionConfig = ActionProcessor.buildActionObject(
      {
        viewName,
        className,
        referenceListStr,
        subscriptName
      },
      this.c11nEnv
    );
    const theType = actionType.SHOW_PAGE;
    const { promise } = this.actionsApiPromise(theType, actionConfig);

    StateManager.dispatch({
      type: theType,
      payload: {
        ...actionConfig,
        containerName: publicConstants.MODAL,
        skipSemanticUrl: true,
        isCreateDataObject: true
      }
    });
    // return the Promise associated with this action
    return promise;
  }

  /**
   * Deletes a work object. Shows a confirmation dialog before deletion
   * unless skipDeleteConfirmation is set.
   *
   * @example <caption>In this example, the success callback is called if object is deleted.</caption>
   *
   * getPConnect().getActionsApi().deleteWork('ON8TTL-C11nGall-Work C-663002', 'ON8TTL-C11nGall-Work-LocalActionsTest').then(() => {
   *  // deleteWork success handling
   * }).catch(() => {
   *  // deleteWork failure handling
   * });
   *
   * @param workId - The work ID to delete
   * @param caseTypeID - The case type ID
   * @param options
   * @param options.skipDeleteConfirmation - If true, skips the delete confirmation dialog and deletes directly
   * @param options.enableBackNavigation - If true, navigates back to the previous screen after successful deletion
   *
   */
  deleteWork(
    workId: string,
    caseTypeID: string,
    options?: { skipDeleteConfirmation?: boolean; enableBackNavigation?: boolean }
  ) {
    if (!workId) {
      return Promise.reject(new Error('workId is required parameter'));
    }
    const theType = actionType.DELETE_WORK;
    const isExternalCase = CaseUtils.isExternalCaseType(caseTypeID);
    const actionConfig = ActionProcessor.buildActionObject(
      {
        workId,
        caseTypeID,
        isExternalCase,
        ...(options?.skipDeleteConfirmation && { skipDeleteConfirmation: true }),
        ...(options?.enableBackNavigation && { enableBackNavigation: true })
      },
      this.c11nEnv
    );
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    StateManager.dispatch(deleteWorkAction({ ...actionConfig }));
    return promise;
  }

  /**
   * Deletes a data record. Shows a confirmation dialog before deletion.
   *
   * @example <caption>In this example, the success callback is called if the data record is deleted.</caption>
   *
   * getPConnect().getActionsApi().deleteDataRecord('MyDataClass', { pyGUID: '...' }).then(() => {
   *  // deleteDataRecord success handling
   * }).catch(() => {
   *  // deleteDataRecord failure handling
   * });
   *
   * @param className - The class name of the data object to delete
   * @param keys - Key-value pairs identifying the data object to delete
   *
   */
  deleteDataRecord(className: string, keys: Record<string, unknown>) {
    if (!className) {
      return Promise.reject(new Error('className is required parameter'));
    }

    if (!keys) {
      return Promise.reject(new Error('keys is required parameter'));
    }

    const theType = actionType.DELETE_WORK;
    const actionConfig = ActionProcessor.buildActionObject({ className, keys }, this.c11nEnv);
    const { promise } = this.actionsApiPromise(theType, actionConfig);
    StateManager.dispatch(deleteWorkAction({ ...actionConfig }));
    return promise;
  }
}

export default ActionsApi;
